import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { GameStatsOverlay } from './GameStatsOverlay';
import { FloatingVolumeControl } from './FloatingVolumeControl';
import { Settings } from './Settings';
import { IntegrationSettings } from './IntegrationSettings';
import { PushToTalkSettings } from './PushToTalkSettings';
import { ChatCommandSettings } from './ChatCommandSettings';
import { ChannelPointsSettings } from './ChannelPointsSettings';
import { ProfanityFilterSettings } from './ProfanityFilterSettings';
import { ViewerMemorySettings } from './ViewerMemorySettings';
import { ActivityLog } from './ActivityLog';
import { AnalyticsDashboard } from './Dashboard';
import { MicPermissionModal } from './MicPermissionModal';
import { PrivacyWarningModal } from './PrivacyWarningModal';
import { ScreenPickerModal } from './ScreenPickerModal';
import type { ScreenSource } from './ScreenPickerModal';
import { useWebSocket } from '../hooks/useWebSocket';
import { useAudioCapture } from '../hooks/useAudioCapture';
import { useAudioPlayback } from '../hooks/useAudioPlayback';
import { usePushToTalk } from '../hooks/usePushToTalk';
import { useVisionCapture } from '../hooks/useVisionCapture';
import { useDashboardSettings } from '../hooks/useDashboardSettings';
import { useSessionStore } from '../stores/sessionStore';
import { PageBackground } from './PageBackground';
import { CriticalBanner } from './CriticalBanner';
import { transitionPttAck, transitionPttStartAck, transitionPttStartTimeout, transitionPttTimeout } from './pttAckSync';
import { deriveHandsFreeState, derivePttState } from './voiceState';
import { logger } from '../utils/logger';
import { AUDIO_INPUT_SAMPLE_RATE } from '@stream-buddy/shared';
import { parseSubscriptionStatus, normalizeSubscriptionStatus, getQuickStartDefaultSection, getDashboardReadiness, type SubscriptionStatus, type DashboardSection } from '../utils/dashboardReadiness';
export { parseSubscriptionStatus, normalizeSubscriptionStatus, getQuickStartDefaultSection, getDashboardReadiness } from '../utils/dashboardReadiness';
export type { SubscriptionStatus, DashboardSection } from '../utils/dashboardReadiness';

interface User {
  username: string;
  displayName: string;
  avatarUrl: string;
  subscriptionStatus: SubscriptionStatus;
}

interface GameStat {
  value: number;
  label?: string;
  updatedAt: number;
  lastUpdateMetadata?: string;
}



export function DesktopDashboard() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectionToken, setConnectionToken] = useState<string | null>(null);
  const [widgetToken, setWidgetToken] = useState<string | null>(null);
  const [channel, setChannel] = useState<string | null>(null);
  const [showMicPermissionModal, setShowMicPermissionModal] = useState(false);
  const [appVersion, setAppVersion] = useState('1.0.0');

  const serverUrl = import.meta.env.DEV
    ? (window.location.hostname.includes('localhost') || window.location.hostname.includes('127.0.0.1')
      ? `${window.location.protocol}//localhost:3001`
      : 'http://127.0.0.1:3001')
    : `${window.location.protocol}//${window.location.host}`;

  // Fetch app version from Electron
  useEffect(() => {
    if (window.electronAPI?.getVersion) {
      window.electronAPI.getVersion().then(setAppVersion).catch(() => { });
    }
  }, []);

  // Check microphone permission on mount
  useEffect(() => {
    async function checkMicPermission() {
      try {
        // Try to query permission status (not supported in all browsers)
        if (navigator.permissions && navigator.permissions.query) {
          const result = await navigator.permissions.query({ name: 'microphone' });
          if (result.state === 'prompt') {
            setShowMicPermissionModal(true);
          }
        } else {
          // Fallback: try to get user media and see if it fails
          try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(track => track.stop());
          } catch {
            setShowMicPermissionModal(true);
          }
        }
      } catch (err) {
        console.warn('[Desktop] Failed to fetch widget token:', err);
      }
    }
    void checkMicPermission();
  }, []);

  useEffect(() => {
    // Check if user is logged in
    fetch(`${serverUrl}/auth/me`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then((data: AuthMeResponse | null) => {
        if (data && !data.error) {
          setUser({
            username: data.twitchUsername || '',
            displayName: data.twitchDisplayName || data.twitchUsername || 'Streamer',
            avatarUrl: data.avatarUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(data.twitchUsername || 'User')}&background=9333ea&color=fff`,
            subscriptionStatus: normalizeSubscriptionStatus(data.subscriptionStatus),
          });
          // Get connection token for dashboard WebSocket
          fetch(`${serverUrl}/auth/token`, { credentials: 'include' })
            .then(res => res.json() as Promise<TokenResponse>)
            .then(tokenData => {
              setConnectionToken(tokenData.token ?? null);
              setChannel(tokenData.channel ?? null);
            })
            .catch((err: unknown) => console.warn('[Desktop] Failed to fetch connection token:', err));

          // Get widget token for overlay browser source URL (persistent token)
          fetch(`${serverUrl}/auth/widget-token`, { credentials: 'include' })
            .then(res => res.json() as Promise<WidgetTokenResponse>)
            .then(tokenData => {
              setWidgetToken(tokenData.widgetToken ?? null);
            })
            .catch((err: unknown) => console.warn('Failed to fetch widget token:', err));
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [serverUrl]);

  const handleLogin = () => {
    window.location.href = `${serverUrl}/auth/twitch`;
  };

  const handleLogout = () => {
    fetch(`${serverUrl}/auth/logout`, { method: 'POST', credentials: 'include' })
      .then(() => {
        setUser(null);
        setConnectionToken(null);
      })
      .catch((err) => console.error('[Desktop] Logout failed:', err));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center relative overflow-hidden">
        <PageBackground />
        <div className="text-white text-xl animate-pulse relative z-10 font-light tracking-widest">LOADING STREAM BUDDY...</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-[#0a0a0f] flex flex-col relative overflow-hidden selection:bg-purple-500/30">
      {/* Microphone Permission Modal */}
      {showMicPermissionModal && (
        <MicPermissionModal
          onGranted={() => setShowMicPermissionModal(false)}
          onDismiss={() => setShowMicPermissionModal(false)}
        />
      )}
      <PageBackground />
      <CriticalBanner />

      {/* Header - draggable for frameless window */}
      <header className="relative shrink-0 z-50 py-3 px-6 flex justify-between items-center bg-[#0a0a0f]/60 backdrop-blur-md border-b border-white/5 transition-all duration-300" style={{ WebkitAppRegion: 'drag' } as React.CSSProperties}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          </div>
          <div
            className="flex items-baseline gap-0.5 select-none group cursor-pointer transition-transform hover:scale-[1.02] duration-300"
            onClick={() => window.open('https://aistreambuddy.com')}
            style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
          >
            <span className="text-sm font-extrabold text-white transition-colors mb-0.5">ai</span>
            <h1 className="text-xl font-bold text-white tracking-tight">
              Stream<span className="text-purple-400 font-light">Buddy</span>
            </h1>
            <span className="text-xs font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 opacity-80 group-hover:opacity-100 transition-opacity">.com</span>
          </div>
        </div>

        <div className="flex items-center gap-4" style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}>
          {user && (
            <details className="group relative">
              <summary className="flex cursor-pointer list-none items-center gap-3 rounded-2xl border border-transparent px-2 py-1 transition-all hover:border-cyan-300/[0.14] hover:bg-white/[0.045] [&::-webkit-details-marker]:hidden">
                <div className="relative">
                  <img
                    src={user.avatarUrl}
                    alt={user.displayName}
                    width={42}
                    height={42}
                    className="h-[42px] w-[42px] rounded-full border-2 border-violet-400 shadow-[0_0_22px_rgba(139,92,246,0.36)]"
                  />
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 rounded-md border border-violet-200/30 bg-[#0b1020] px-1.5 py-0.5 text-[9px] font-black uppercase leading-none text-white shadow-[0_0_14px_rgba(139,92,246,0.35)]">
                    {user.subscriptionStatus === 'active' ? 'Pro' : user.subscriptionStatus === 'past_due' ? 'Due' : 'Free'}
                  </span>
                </div>
                <div className="hidden min-w-0 sm:flex sm:h-[42px] sm:items-center">
                  <p className="max-w-36 truncate text-sm font-black leading-tight text-white">{user.displayName}</p>
                </div>
                <svg className="hidden h-4 w-4 text-slate-400 transition-transform group-open:rotate-180 sm:block" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="m5.5 7.5 4.5 4.5 4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </summary>

              <div className="absolute right-0 top-[calc(100%+0.6rem)] z-50 w-56 overflow-hidden rounded-2xl border border-cyan-300/[0.14] bg-[#070d1c]/95 p-2 shadow-[0_24px_70px_rgba(0,0,0,0.48)] backdrop-blur-xl">
                <div className="border-b border-white/10 px-3 py-2.5">
                  <p className="truncate text-sm font-black text-white">{user.displayName}</p>
                  <p className="truncate text-xs font-medium text-slate-400">@{user.username}</p>
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="mt-2 flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm font-bold text-slate-300 transition hover:bg-rose-500/10 hover:text-rose-100"
                >
                  <span>Logout</span>
                  <span aria-hidden="true">-&gt;</span>
                </button>
              </div>
            </details>
          )}
          {/* Window controls for Electron */}
          {window.electronAPI && (
            <div className="flex items-center ml-4 gap-1">
              <button
                onClick={() => window.electronAPI?.windowMinimize()}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors"
              >
                <svg width="12" height="2" viewBox="0 0 12 2" fill="currentColor"><rect width="12" height="2" /></svg>
              </button>
              <button
                onClick={() => window.electronAPI?.windowMaximize()}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="0.75" y="0.75" width="8.5" height="8.5" /></svg>
              </button>
              <button
                onClick={() => window.electronAPI?.windowClose()}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-red-500/80 rounded transition-colors"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.5" /></svg>
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="flex-1 overflow-hidden relative z-10">
        <main className="mx-auto h-full max-w-none px-3 py-3 sm:px-4">
          {!user ? (
            <DesktopLoginSection onLogin={handleLogin} />
          ) : (
            <DesktopDashboardContent
              user={user}
              connectionToken={connectionToken}
              widgetToken={widgetToken}
              channel={channel}
              serverUrl={serverUrl}
            />
          )}
        </main>
      </div>

      <footer className="shrink-0 px-4 py-3 text-center border-t border-white/5 bg-black/20 backdrop-blur-md relative z-10">
        <p className="text-gray-500 text-xs font-medium">
          &copy; 2026 <span className="text-purple-400/70">Stream Buddy</span>. <span className="text-gray-600">v{appVersion}</span>
        </p>
      </footer>
    </div>
  );
}

interface AuthMeResponse {
  twitchUsername?: string;
  twitchDisplayName?: string;
  avatarUrl?: string;
  subscriptionStatus?: string;
  error?: string;
}

interface TokenResponse {
  token?: string;
  channel?: string;
}

interface WidgetTokenResponse {
  widgetToken?: string;
}

interface SubscriptionResponse {
  status?: string;
  subscriptionStatus?: string;
}

interface TwitchValidateResponse {
  valid?: boolean;
  error?: string;
}

interface SessionStatusResponse {
  connected?: boolean;
  muted?: boolean;
  muteExpiresAt?: number | null;
  turnPhase?: 'idle' | 'capturing' | 'flushing' | 'awaiting_model' | 'playing' | 'finalizing';
  isStreamerSpeaking?: boolean;
  overlayConnected?: boolean;
}

function DesktopLoginSection({ onLogin }: { onLogin: () => void }) {
  return (
    <div className="max-w-md mx-auto text-center py-24">
      <div className="p-8 rounded-3xl bg-white/5 border border-white/10 backdrop-blur-xl shadow-2xl">
        <div className="w-20 h-20 bg-purple-600 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-lg shadow-purple-500/30">
          <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-white mb-3 tracking-tight">Stream Buddy</h2>
        <p className="text-gray-400 mb-8 leading-relaxed">
          Your AI-powered streaming companion. <br />Connect with Twitch to start.
        </p>
        <button
          onClick={onLogin}
          className="w-full bg-[#6441a5] hover:bg-[#7d5bbe] text-white font-bold py-4 px-8 rounded-xl text-lg transition-all flex items-center justify-center gap-3 shadow-lg hover:shadow-purple-500/20 transform hover:-translate-y-0.5"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
          </svg>
          Login with Twitch
        </button>
      </div>
    </div>
  );
}

interface DesktopDashboardContentProps {
  user: User;
  connectionToken: string | null;
  widgetToken: string | null;
  channel: string | null;
  serverUrl: string;
}

// Dashboard navigation sections
type BackgroundStyle = 'nebula' | 'cosmic' | 'stars';

function DesktopDashboardContent({ user, connectionToken, widgetToken, channel, serverUrl }: DesktopDashboardContentProps) {
  const { isOverlayConnected, setOverlayConnected, isReconnecting, reconnectFailed, isChatAuthenticated, isGeminiConnected, clearTranscript, backgroundStyle, setBackgroundStyle, gameStats, isAiSpeaking, isAiThinking } = useSessionStore();
  const [sessionStatus, setSessionStatus] = useState<{
    connected: boolean;
    muted: boolean;
    muteExpiresAt: number | null;
    turnPhase: 'idle' | 'capturing' | 'flushing' | 'awaiting_model' | 'playing' | 'finalizing';
    isStreamerSpeaking: boolean;
  } | null>(null);
  const sessionStatusRef = useRef(sessionStatus);
  const sessionStatusUpdatedAtRef = useRef(0);
  const [isLive, setIsLive] = useState(false);
  const [wentLiveAt, setWentLiveAt] = useState<number | null>(null);
  const [reconnectedAt, setReconnectedAt] = useState<number | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [pttDebug, setPttDebug] = useState<{
    turnPhase: 'idle' | 'capturing' | 'flushing' | 'awaiting_model' | 'playing' | 'finalizing';
    lastEvent: string;
    lastReason: string | null;
    lastTraceId: string | null;
    updatedAt: number;
  }>({
    turnPhase: 'idle',
    lastEvent: 'none',
    lastReason: null,
    lastTraceId: null,
    updatedAt: 0,
  });
  const wsConnectedRef = useRef(false);
  const socketConnectedAtRef = useRef(0);
  const liveStartRequestedAtRef = useRef(0);
  const isLiveRef = useRef(false);
  const isCapturingRef = useRef(false);
  const sessionStartRequestedRef = useRef(false);
  const liveSessionStartRetryCountRef = useRef(0);
  const liveSessionStartRetriesExhaustedRef = useRef(false);
  const hasConnectedBeforeRef = useRef(false); // Track if we've connected before (for reconnect detection)
  const sendAudioRef = useRef<((data: ArrayBuffer) => void) | null>(null);
  const startCaptureRef = useRef<(() => Promise<void>) | null>(null);
  const stopCaptureRef = useRef<(() => Promise<void>) | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [currentSubscriptionStatus, setCurrentSubscriptionStatus] = useState<string | null>(null);
  const [subscriptionRejected, setSubscriptionRejected] = useState(false);

  // Vision Screen Picker State
  const [showScreenPicker, setShowScreenPicker] = useState(false);
  const [desktopSources, setDesktopSources] = useState<ScreenSource[]>([]);
  const [isLoadingSources, setIsLoadingSources] = useState(false);
  const [showPrivacyWarning, setShowPrivacyWarning] = useState(false);

  // Sidebar navigation state
  const [quickStartHidden, setQuickStartHidden] = useState(() => localStorage.getItem('streambuddy_quickstart_hidden') === 'true');
  const [activeSection, setActiveSection] = useState<DashboardSection>(() => {
    return 'home';
  });
  const [showUrl, setShowUrl] = useState(false);
  const subscriptionExpired = subscriptionRejected || (currentSubscriptionStatus !== null && currentSubscriptionStatus !== 'active');

  // Quick Start completion tracking
  const [hasStreamlabsToken, setHasStreamlabsToken] = useState(() => {
    return localStorage.getItem('streambuddy_has_streamlabs') === 'true';
  });
  const [hasVisitedPtt, setHasVisitedPtt] = useState(() => {
    return localStorage.getItem('streambuddy_visited_ptt') === 'true';
  });
  const [hasVisitedAiVoice, setHasVisitedAiVoice] = useState(() => {
    return localStorage.getItem('streambuddy_visited_ai_voice') === 'true';
  });

  // Track when user visits PTT section or configures Streamlabs
  const handleSectionChange = useCallback((section: DashboardSection) => {
    setActiveSection(section);
    if (section === 'ptt' && !hasVisitedPtt) {
      setHasVisitedPtt(true);
      localStorage.setItem('streambuddy_visited_ptt', 'true');
    }
    if (section === 'ai-voice' && !hasVisitedAiVoice) {
      setHasVisitedAiVoice(true);
      localStorage.setItem('streambuddy_visited_ai_voice', 'true');
    }
  }, [hasVisitedAiVoice, hasVisitedPtt]);

  const hideQuickStart = useCallback(() => {
    setQuickStartHidden(true);
    localStorage.setItem('streambuddy_quickstart_hidden', 'true');
    setActiveSection(current => current === 'quickstart' ? getQuickStartDefaultSection(isLiveRef.current) : current);
  }, []);

  const showQuickStart = useCallback(() => {
    setQuickStartHidden(false);
    localStorage.setItem('streambuddy_quickstart_hidden', 'false');
    setActiveSection('quickstart');
  }, []);

  const toggleQuickStart = useCallback(() => {
    setQuickStartHidden(current => {
      const nextHidden = !current;
      localStorage.setItem('streambuddy_quickstart_hidden', nextHidden ? 'true' : 'false');
      setActiveSection(currentSection => {
        if (!nextHidden) return 'quickstart';
        return currentSection === 'quickstart' ? getQuickStartDefaultSection(isLiveRef.current) : currentSection;
      });
      return nextHidden;
    });
  }, []);

  // Callback for IntegrationSettings to notify when streamlabs is configured
  const handleStreamlabsConfigured = useCallback((hasToken: boolean) => {
    setHasStreamlabsToken(hasToken);
    localStorage.setItem('streambuddy_has_streamlabs', hasToken ? 'true' : 'false');
  }, []);

  useEffect(() => {
    if (quickStartHidden && activeSection === 'quickstart') {
      setActiveSection(getQuickStartDefaultSection(isLive));
    }
  }, [activeSection, isLive, quickStartHidden]);

  // Toast notification state for user feedback
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    // Clear any existing toast timeout to prevent stacking
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast({ message, type });
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  // Overlay URL copy - uses widget token for persistent browser source URLs
  const clientUrl = import.meta.env.DEV ? 'http://localhost:5173' : window.location.origin;
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  const regenerateToken = useCallback(async () => {
    if (isRegenerating) return;
    setShowRegenerateConfirm(false);

    setIsRegenerating(true);
    try {
      const response = await fetch(`${serverUrl}/auth/widget-token/regenerate`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to regenerate token');
      }

      // Refresh the page to get the new token
      showToast('Token regenerated! Copy the new URL for OBS.', 'success');
      window.location.reload();
    } catch {
      showToast('Failed to regenerate token. Please try again.', 'error');
    } finally {
      setIsRegenerating(false);
    }
  }, [serverUrl, showToast, isRegenerating]);

  const handleRegenerateClick = useCallback(() => {
    setShowRegenerateConfirm(true);
  }, []);

  const copyToClipboard = useCallback(async () => {
    if (!widgetToken) {
      showToast('Widget token not available. Please try again.', 'error');
      return;
    }
    const url = `${clientUrl}/overlay?widgetToken=${widgetToken}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('Overlay URL copied to clipboard!', 'success');
    } catch {
      // Fallback for browsers that don't support clipboard API or non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        showToast('Overlay URL copied to clipboard!', 'success');
      } catch {
        showToast('Failed to copy URL. Please copy manually.', 'error');
      }
      document.body.removeChild(textArea);
    }
  }, [clientUrl, widgetToken, showToast]);

  // PTT settings - use localStorage for immediate availability on hot reload
  const settingsLoadedRef = useRef(false);
  // Audio loopback for debugging - hear what the AI hears
  const [loopbackEnabled, setLoopbackEnabled] = useState(false);
  const loopback = useAudioPlayback({
    sampleRate: AUDIO_INPUT_SAMPLE_RATE,
    prebufferMs: 80,
    lowBufferThresholdMs: 5,
    fadeMs: 8,
  });

  // Chat command settings
  const handleSettingsReady = useCallback(() => {
    settingsLoadedRef.current = true;
  }, []);
  const dashboardSettings = useDashboardSettings({
    onError: message => showToast(message, 'error'),
    onReady: handleSettingsReady,
  });
  const { micId: selectedMicId, inputGain, outputVolume, isMuted } = dashboardSettings.audio;
  const { pttEnabled, pttHotkey, pttHotkeyDisplay, visualsEnabled, overlayScale } = dashboardSettings.audio;
  const { allowViewerMessages, prefix: commandPrefix, modOnly, cooldown: commandCooldown, sendCooldownMessages } = dashboardSettings.commands;
  const { enabled: channelPointsEnabled, rewardIds: channelPointsRewardIds } = dashboardSettings.channelPoints;
  const { assistantName, voiceName: personaVoiceName, onDemandVision } = dashboardSettings.persona;
  const settingsLoaded = dashboardSettings.status === 'ready';
  const updateDashboardSettings = dashboardSettings.update;
  const handleMicChange = useCallback((deviceId: string) => updateDashboardSettings({ type: 'mic', deviceId }), [updateDashboardSettings]);
  const handleInputGainChange = useCallback((gain: number) => updateDashboardSettings({ type: 'inputGain', gain }), [updateDashboardSettings]);
  const handleVolumeChange = useCallback((volume: number) => updateDashboardSettings({ type: 'volume', volume }), [updateDashboardSettings]);
  const handleMuteToggle = dashboardSettings.toggleMute;
  const handlePttHotkeyChange = useCallback((hotkey: string, display: string) => updateDashboardSettings({ type: 'pttHotkey', hotkey, display }), [updateDashboardSettings]);
  const handleVisualsEnabledChange = useCallback((enabled: boolean) => updateDashboardSettings({ type: 'visualsEnabled', enabled }), [updateDashboardSettings]);
  const handleOverlayScaleChange = useCallback((scale: number) => updateDashboardSettings({ type: 'overlayScale', scale }), [updateDashboardSettings]);
  const handleAllowViewerMessagesChange = useCallback((enabled: boolean) => updateDashboardSettings({ type: 'allowViewerMessages', enabled }), [updateDashboardSettings]);
  const handleCommandPrefixChange = useCallback((prefix: string) => updateDashboardSettings({ type: 'commandPrefix', prefix }), [updateDashboardSettings]);
  const handleModOnlyChange = useCallback((enabled: boolean) => updateDashboardSettings({ type: 'modOnly', enabled }), [updateDashboardSettings]);
  const handleCooldownChange = useCallback((seconds: number) => updateDashboardSettings({ type: 'cooldown', seconds }), [updateDashboardSettings]);
  const handleSendCooldownMessagesChange = useCallback((send: boolean) => updateDashboardSettings({ type: 'sendCooldownMessages', send }), [updateDashboardSettings]);
  const handleChannelPointsEnabledChange = useCallback((enabled: boolean) => updateDashboardSettings({ type: 'channelPointsEnabled', enabled }), [updateDashboardSettings]);
  const handleChannelPointsRewardIdsChange = useCallback((ids: string[]) => updateDashboardSettings({ type: 'channelPointRewardIds', ids }), [updateDashboardSettings]);
  const setAssistantName = useCallback((name: string) => updateDashboardSettings({ type: 'assistantName', name }), [updateDashboardSettings]);
  const setPersonaVoiceName = useCallback((voiceName: string) => updateDashboardSettings({ type: 'personaVoiceName', voiceName }), [updateDashboardSettings]);
  const dashboardReadiness = getDashboardReadiness({ isLive, subscriptionExpired, isOverlayConnected, assistantName, pttEnabled, pttHotkeyDisplay });
  const dashboardReadinessStyle = dashboardReadiness.tone === 'warning'
    ? { border: 'border-amber-300/25', background: 'bg-amber-400/10', dot: 'bg-amber-300 shadow-[0_0_14px_rgba(251,191,36,0.58)]', text: 'text-amber-200' }
    : dashboardReadiness.tone === 'blocked'
      ? { border: 'border-rose-300/25', background: 'bg-rose-400/10', dot: 'bg-rose-300 shadow-[0_0_14px_rgba(251,113,133,0.58)]', text: 'text-rose-200' }
      : { border: 'border-emerald-300/20', background: 'bg-emerald-400/10', dot: isLive ? 'bg-emerald-300 animate-pulse shadow-[0_0_18px_rgba(52,211,153,0.8)]' : 'bg-emerald-400 shadow-[0_0_14px_rgba(52,211,153,0.55)]', text: 'text-emerald-300' };

  // Twitch connection status
  const [twitchStatus, setTwitchStatus] = useState<'checking' | 'valid' | 'expired' | 'error'>('checking');
  // Debounced display status to prevent "Relogin" flash during transitions
  const [displayTwitchStatus, setDisplayTwitchStatus] = useState<'checking' | 'valid' | 'expired' | 'error'>('checking');
  // Track when reconnection finishes to add grace period
  const wasReconnectingRef = useRef(isReconnecting);
  useEffect(() => {
    if (!isReconnecting && wasReconnectingRef.current) {
      // Just finished reconnecting
      setReconnectedAt(Date.now());
    }
    wasReconnectingRef.current = isReconnecting;
  }, [isReconnecting]);

  // Debounce the display status - very short delay to avoid flash during reconnect
  // Only delays "expired" briefly; if status stays expired, it will show
  useEffect(() => {
    if (twitchStatus === 'valid' || twitchStatus === 'checking') {
      // Immediately show valid/checking
      setDisplayTwitchStatus(twitchStatus);
    } else if (twitchStatus === 'expired' || twitchStatus === 'error') {
      // Very short delay to catch brief flashes during reconnect
      // If auth is genuinely invalid, this will still show after 100ms
      const timer = setTimeout(() => {
        setDisplayTwitchStatus(twitchStatus);
      }, 100); // 100ms delay - just enough to catch flash
      return () => clearTimeout(timer);
    }
  }, [twitchStatus]);

  // Validate Twitch OAuth status
  // Uses session store isChatAuthenticated as primary source (server knows best)
  // Falls back to API validation for edge cases
  useEffect(() => {
    if (!user) {
      setTwitchStatus('checking');
      return;
    }

    // If server reports chat is not authenticated (no OAuth token), show expired
    // BUT: add grace periods:
    // 1. After going live - Twitch connection takes 3-5 seconds to establish
    // 2. After reconnecting - Twitch reconnection takes time
    if (!isChatAuthenticated) {
      const GRACE_PERIOD_MS = 5000; // 5 seconds grace period
      const isInLiveGracePeriod = wentLiveAt && Date.now() - wentLiveAt < GRACE_PERIOD_MS;
      const isInReconnectGracePeriod = reconnectedAt && Date.now() - reconnectedAt < GRACE_PERIOD_MS;

      if (isInLiveGracePeriod || isInReconnectGracePeriod) {
        // Still connecting to Twitch, don't show expired yet
        setTwitchStatus('checking');
        return;
      }

      setTwitchStatus('expired');
      logger.log('Twitch OAuth: Not authenticated (server reports no token)');
      return;
    }

    // Chat is authenticated, validate token via API to catch expired tokens
    const validateTwitch = async () => {
      try {
        const res = await fetch(`${serverUrl}/auth/twitch/validate`, { credentials: 'include' });
        if (res.ok) {
          const data = (await res.json()) as TwitchValidateResponse;
          if (data.valid) {
            setTwitchStatus('valid');
          } else {
            setTwitchStatus('expired');
            logger.log('Twitch token invalid:', data.error);
          }
        } else if (res.status === 401) {
          // User not authenticated - wait for login
          setTwitchStatus('checking');
        } else {
          // Server error - trust the session store status
          console.error('Twitch validation error:', res.status);
          setTwitchStatus('valid'); // Assume valid since isChatAuthenticated is true
        }
      } catch (e) {
        console.error('Failed to validate Twitch token:', e);
        // Network error - trust the session store status
        setTwitchStatus('valid'); // Assume valid since isChatAuthenticated is true
      }
    };
    void validateTwitch();
  }, [user, serverUrl, isChatAuthenticated, isLive, wentLiveAt, reconnectedAt]);

  // Fetch fresh subscription status from server
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const res = await fetch('/api/billing/subscription', { credentials: 'include' });
        if (res.ok) {
          const data = (await res.json()) as SubscriptionResponse;
          const status = parseSubscriptionStatus(data.status || data.subscriptionStatus);
          if (status) {
            setCurrentSubscriptionStatus(status);
            if (status === 'active') {
              setSubscriptionRejected(false);
            } else {
              setSubscriptionRejected(true);
            }
          }
        }
      } catch (e) {
        console.error('Failed to fetch subscription status:', e);
      }
    };
    void fetchSubscription();

    const interval = setInterval(() => { void fetchSubscription(); }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Poll session status for overlay connection (consolidated - handles both overlay and session status)
  useEffect(() => {
    const fetchSessionStatus = async () => {
      try {
        const res = await fetch('/api/session/status', { credentials: 'include' });
        if (res.ok) {
          const data = (await res.json()) as SessionStatusResponse;
          setOverlayConnected(data.overlayConnected ?? false);
          // Also update the session status from this same endpoint
          sessionStatusUpdatedAtRef.current = Date.now();
          setSessionStatus({
            connected: data.connected ?? false,
            muted: data.muted ?? false,
            muteExpiresAt: data.muteExpiresAt ?? null,
            turnPhase: data.turnPhase ?? 'idle',
            isStreamerSpeaking: data.isStreamerSpeaking ?? false,
          });
        }
      } catch (e) {
        console.error('Failed to fetch session status:', e);
      }
    };
    void fetchSessionStatus();

    const interval = setInterval(() => { void fetchSessionStatus(); }, 3000);
    return () => clearInterval(interval);
  }, [setOverlayConnected]);

  // Keep sessionStatusRef in sync for use in onConnected callback
  useEffect(() => {
    sessionStatusRef.current = sessionStatus;
  }, [sessionStatus]);

  const wsUrl = connectionToken ? `${serverUrl.replace('http', 'ws')}/ws?token=${connectionToken}&channel=${channel}` : null;

  // Open billing URLs in system browser
  const openBillingPortal = (e: React.MouseEvent) => {
    const url = `${serverUrl}/api/billing/portal`;
    if (window.electronAPI?.openExternal) {
      e.preventDefault();
      window.electronAPI.openExternal(url);
    }
  };

  const pendingCaptureOnPttAckRef = useRef(false);
  const expectedPttTraceIdRef = useRef<string | null>(null);
  const pttAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestLiveSessionStartRef = useRef<(() => void) | null>(null);
  const liveSessionRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const MAX_LIVE_SESSION_START_RETRIES = 4;

  const applyPttAckSyncState = useCallback((state: { pendingCaptureOnAck: boolean; expectedTraceId: string | null }) => {
    pendingCaptureOnPttAckRef.current = state.pendingCaptureOnAck;
    expectedPttTraceIdRef.current = state.expectedTraceId;
  }, []);

  const clearPttAckWait = useCallback(() => {
    if (pttAckTimeoutRef.current) {
      clearTimeout(pttAckTimeoutRef.current);
      pttAckTimeoutRef.current = null;
    }
    applyPttAckSyncState({ pendingCaptureOnAck: false, expectedTraceId: null });
  }, [applyPttAckSyncState]);

  const startCaptureAfterPttSync = useCallback((reason: 'ack' | 'timeout') => {
    clearPttAckWait();
    if (!isCapturingRef.current && startCaptureRef.current) {
      requestAnimationFrame(() => {
        if (startCaptureRef.current) { void startCaptureRef.current(); }
      });
    }
    if (reason === 'timeout') {
      console.warn('[Desktop] PTT sync acknowledgment timed out, continuing with capture fallback');
    }
  }, [clearPttAckWait]);

  useEffect(() => {
    return () => {
      if (pttAckTimeoutRef.current) {
        clearTimeout(pttAckTimeoutRef.current);
      }
      if (liveSessionRetryTimeoutRef.current) {
        clearTimeout(liveSessionRetryTimeoutRef.current);
      }
    };
  }, []);

  const pttStartAckTraceRef = useRef<string | null>(null);
  const pttStartAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const delayedPttStartTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pttStartSentRef = useRef(false);
  const pttEndAckTraceRef = useRef<string | null>(null);
  const pttEndAckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sendEndAudioTurnRef = useRef<(traceId?: string) => void>(() => undefined);
  const unmuteRef = useRef<() => void | Promise<void>>(() => undefined);

  const clearPttStartAckWait = useCallback(() => {
    if (pttStartAckTimeoutRef.current) {
      clearTimeout(pttStartAckTimeoutRef.current);
      pttStartAckTimeoutRef.current = null;
    }
    pttStartAckTraceRef.current = null;
    pttStartSentRef.current = false;
  }, []);

  const clearDelayedPttStartWait = useCallback(() => {
    if (delayedPttStartTimeoutRef.current) {
      clearTimeout(delayedPttStartTimeoutRef.current);
      delayedPttStartTimeoutRef.current = null;
    }
  }, []);

  const clearPttEndAckWait = useCallback(() => {
    if (pttEndAckTimeoutRef.current) {
      clearTimeout(pttEndAckTimeoutRef.current);
      pttEndAckTimeoutRef.current = null;
    }
    pttEndAckTraceRef.current = null;
  }, []);

  const handleMessage = useCallback((message: {
    type: string;
    data?: string;
    error?: string;
    code?: string;
    message?: string;
    connected?: boolean;
    isStopping?: boolean;
    geminiConnected?: boolean;
    muted?: boolean;
    muteExpiresAt?: number | null;
    enabled?: boolean;
    traceId?: string;
    accepted?: boolean;
    reason?: string;
    event?: 'phase_changed' | 'turn_start' | 'capture_ended' | 'model_first_audio' | 'playback_started' | 'playback_ended' | 'turn_complete' | 'turn_failed';
    phase?: 'idle' | 'capturing' | 'flushing' | 'awaiting_model' | 'playing' | 'finalizing';
    turnPhase?: 'idle' | 'capturing' | 'flushing' | 'awaiting_model' | 'playing' | 'finalizing';
    isStreamerSpeaking?: boolean;
  }) => {
    if (message.type === 'status') {
      sessionStatusUpdatedAtRef.current = Date.now();
      setSessionStatus({
        connected: message.connected ?? false,
        muted: message.muted ?? false,
        muteExpiresAt: message.muteExpiresAt ?? null,
        turnPhase: message.phase ?? message.turnPhase ?? 'idle',
        isStreamerSpeaking: message.isStreamerSpeaking ?? false,
      });
      setPttDebug((prev) => ({
        ...prev,
        turnPhase: message.phase ?? message.turnPhase ?? 'idle',
        updatedAt: Date.now(),
      }));

      if (message.connected) {
        sessionStartRequestedRef.current = false;
        liveSessionStartRetryCountRef.current = 0;
        liveSessionStartRetriesExhaustedRef.current = false;
        if (liveSessionRetryTimeoutRef.current) {
          clearTimeout(liveSessionRetryTimeoutRef.current);
          liveSessionRetryTimeoutRef.current = null;
        }
      }
    }

    if (message.type === 'audio_output') {
      logger.log('[Desktop] AI Response received (Playback muted in browser)');
    }
    if (message.type === 'session_restarted') {
      logger.log('[Dashboard] Server confirmed restart, restarting audio capture');
      // Only restart audio if we were live before
      // Use requestAnimationFrame to ensure React has updated refs
      if (isLiveRef.current && !isCapturingRef.current && startCaptureRef.current) {
        requestAnimationFrame(() => {
          if (startCaptureRef.current) { void startCaptureRef.current(); }
        });
      }
    }
    if (message.type === 'ptt_updated') {
      const ackTransition = transitionPttAck({
        pendingCaptureOnAck: pendingCaptureOnPttAckRef.current,
        expectedTraceId: expectedPttTraceIdRef.current,
      }, message.traceId);
      if (ackTransition.shouldStartCapture) {
        applyPttAckSyncState(ackTransition.nextState);
        requestLiveSessionStartRef.current?.();
        logger.log('[Desktop] PTT sync acknowledged, starting audio capture', { traceId: message.traceId, enabled: message.enabled });
        startCaptureAfterPttSync('ack');
      }
    }
    if (message.type === 'ptt_start_ack') {
      const startAckTransition = transitionPttStartAck({
        pendingUnmute: pendingUnmuteRef.current,
        expectedTraceId: pttStartAckTraceRef.current,
      }, {
        traceId: message.traceId,
        accepted: message.accepted,
      });

      const traceMatches = !pttStartAckTraceRef.current || message.traceId === pttStartAckTraceRef.current;
      if (!traceMatches) {
        console.warn('[Desktop] Ignoring stale PTT start ack', { expected: pttStartAckTraceRef.current, received: message.traceId });
        return;
      }

      if (pttStartAckTraceRef.current && message.traceId === pttStartAckTraceRef.current) {
        clearPttStartAckWait();
      }
      // CRITICAL: Only unmute after receiving ptt_start_ack to ensure
      // activityStart is sent to Gemini BEFORE audio flows.
      // This fixes the silent completion bug where Gemini ignores audio
      // received before activityStart.
      pendingUnmuteRef.current = startAckTransition.nextState.pendingUnmute;
      pttStartAckTraceRef.current = startAckTransition.nextState.expectedTraceId;
      pttStartSentRef.current = false;
      if (startAckTransition.shouldUnmute && pttPressStartedAtRef.current !== null) {
        void unmuteRef.current();
        logger.log('[Desktop] PTT start acknowledged, unmuting and sending audio');
      } else if (message.accepted === true) {
        sendEndAudioTurnRef.current(`ptt-end-late-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);
      }
      if (message.accepted === false) {
        console.warn('[Desktop] PTT start rejected', { reason: message.reason, traceId: message.traceId });
        setPttDebug((prev) => ({
          ...prev,
          lastEvent: 'ptt_start_rejected',
          lastReason: message.reason ?? null,
          lastTraceId: message.traceId ?? null,
          updatedAt: Date.now(),
        }));
      } else {
        setPttDebug((prev) => ({
          ...prev,
          lastEvent: 'ptt_start_ack',
          lastReason: null,
          lastTraceId: message.traceId ?? null,
          updatedAt: Date.now(),
        }));
      }
    }
    if (message.type === 'ptt_end_ack') {
      if (pttEndAckTraceRef.current && message.traceId === pttEndAckTraceRef.current) {
        clearPttEndAckWait();
      }
      if (message.accepted === false) {
        console.warn('[Desktop] PTT end rejected', { reason: message.reason, traceId: message.traceId });
        setPttDebug((prev) => ({
          ...prev,
          lastEvent: 'ptt_end_rejected',
          lastReason: message.reason ?? null,
          lastTraceId: message.traceId ?? null,
          updatedAt: Date.now(),
        }));
      } else {
        setPttDebug((prev) => ({
          ...prev,
          lastEvent: 'ptt_end_ack',
          lastReason: null,
          lastTraceId: message.traceId ?? null,
          updatedAt: Date.now(),
        }));
      }
    }
    if (message.type === 'request_vision_frame') {
      if (isVisionActiveRef.current && visualsEnabled) {
        captureOneFrameRef.current();
      }
      return;
    }
    if (message.type === 'turn_event') {
      setSessionStatus((prev) => prev ? {
        ...prev,
        turnPhase: message.phase ?? prev.turnPhase,
        isStreamerSpeaking: message.isStreamerSpeaking ?? prev.isStreamerSpeaking,
      } : {
        connected: false,
        muted: false,
        muteExpiresAt: null,
        turnPhase: message.phase ?? 'idle',
        isStreamerSpeaking: message.isStreamerSpeaking ?? false,
      });
      setPttDebug((prev) => {
        return {
          ...prev,
          turnPhase: message.phase ?? prev.turnPhase,
          lastEvent: message.event ?? prev.lastEvent,
          lastReason: message.reason ?? null,
          updatedAt: Date.now(),
        };
      });
    }
    if (message.type === 'error' && (message.error === 'subscription_required' || message.error === 'subscription_expired')) {
      logger.log('[Desktop] Subscription required');
      setCurrentSubscriptionStatus('inactive');
      setSubscriptionRejected(true);
      setIsLive(false);
      setShowUpgradeModal(true);
    }

    if (message.type === 'error' && message.code === 'START_FAILED') {
      console.warn('[Desktop] start_session failed, clearing request latch for retry', { message: message.message });
      sessionStartRequestedRef.current = false;
      if (liveSessionRetryTimeoutRef.current) {
        clearTimeout(liveSessionRetryTimeoutRef.current);
        liveSessionRetryTimeoutRef.current = null;
      }

      liveSessionStartRetryCountRef.current += 1;

      if (liveSessionStartRetryCountRef.current > MAX_LIVE_SESSION_START_RETRIES) {
        liveSessionStartRetriesExhaustedRef.current = true;
        showToast('Failed to start the AI session. Toggle Go Live to try again.', 'error');
        return;
      }

      if (isLiveRef.current && wsConnectedRef.current && settingsLoadedRef.current) {
        const delayMs = Math.min(1000 * (2 ** (liveSessionStartRetryCountRef.current - 1)), 8000);
        liveSessionRetryTimeoutRef.current = setTimeout(() => {
          requestLiveSessionStartRef.current?.();
        }, delayMs);
      }
    }
  }, [applyPttAckSyncState, clearPttEndAckWait, clearPttStartAckWait, showToast, startCaptureAfterPttSync, visualsEnabled]);


  const { connect, disconnect, sendMessage, sendAudio, sendPttStart, sendEndAudioTurn, sendVision, restartSession, startSession, stopSession } = useWebSocket({
    onMessage: handleMessage,
    onConnected: ({ isReconnect }) => {
      logger.log('[Desktop] Connected to server');
      setWsConnected(true);
      wsConnectedRef.current = true;
      socketConnectedAtRef.current = Date.now();
      sessionStatusUpdatedAtRef.current = 0;
      liveSessionStartRetriesExhaustedRef.current = false;
      liveSessionStartRetryCountRef.current = 0;

      // Auto-restart session if we were live before disconnect
      // Use reconnecting state to detect if this was a reconnection attempt
      const wasLive = localStorage.getItem('streambuddy_was_live') === 'true';

      if (wasLive && isLiveRef.current) {
        logger.log('[Desktop] Re-syncing live session after socket connect', { isReconnect });
        // Wait for settings to load to ensure gain and PTT state are correct
        const waitForSettings = () => {
          if (settingsLoadedRef.current) {
            logger.log('[Desktop] Settings loaded, PTT enabled:', pttEnabled);
            // Use requestAnimationFrame to ensure React has rendered
            // and useEffects have updated refs (like inputGainRef and isMuted)
            requestAnimationFrame(() => {
              const traceId = `reconnect-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
              applyPttAckSyncState({ pendingCaptureOnAck: true, expectedTraceId: traceId });
              logger.log('[Desktop] Reconnect trace started', { traceId, pttEnabled });
              if (pttAckTimeoutRef.current) {
                clearTimeout(pttAckTimeoutRef.current);
              }
              pttAckTimeoutRef.current = setTimeout(() => {
                const timeoutTransition = transitionPttTimeout({
                  pendingCaptureOnAck: pendingCaptureOnPttAckRef.current,
                  expectedTraceId: expectedPttTraceIdRef.current,
                });
                if (!timeoutTransition.shouldStartCapture) {
                  return;
                }
                applyPttAckSyncState(timeoutTransition.nextState);
                requestLiveSessionStartRef.current?.();
                startCaptureAfterPttSync('timeout');
              }, 5000);
              sendMessage({ type: 'update_ptt', enabled: pttEnabled, traceId });
            });
          } else {
            logger.log('[Desktop] Waiting for settings to load...');
            setTimeout(waitForSettings, 100);
          }
        };
        waitForSettings();
      }
    },
    onDisconnected: () => {
      logger.log('[Desktop] Disconnected from server');
      sessionStartRequestedRef.current = false;
      sessionStatusUpdatedAtRef.current = 0;
      liveSessionStartRetryCountRef.current = 0;
      liveSessionStartRetriesExhaustedRef.current = false;
      if (liveSessionRetryTimeoutRef.current) {
        clearTimeout(liveSessionRetryTimeoutRef.current);
        liveSessionRetryTimeoutRef.current = null;
      }
      clearPttAckWait();
      setPttDebug({
        turnPhase: 'idle',
        lastEvent: 'socket_disconnected',
        lastReason: null,
        lastTraceId: null,
        updatedAt: Date.now(),
      });
      setWsConnected(false);
      wsConnectedRef.current = false;

      // Check if we were live and should auto-restart
      const wasLive = localStorage.getItem('streambuddy_was_live') === 'true';

      if (isLiveRef.current) {
        logger.log('[Desktop] Unexpected disconnect while live, checking subscription...');
        fetch('/api/billing/subscription', { credentials: 'include' })
          .then(res => res.ok ? res.json() : null)
          .then((data: SubscriptionResponse | null) => {
            const status = parseSubscriptionStatus(data?.status || data?.subscriptionStatus);
            // Only show modal if we confirmed status is non-active (not if status is unknown)
            if (status && status !== 'active') {
              logger.log('[Desktop] Subscription expired, resetting UI');
              setCurrentSubscriptionStatus(status);
              setSubscriptionRejected(true);
              setShowUpgradeModal(true);
              localStorage.removeItem('streambuddy_was_live');
              setIsLive(false);
            } else if (!status) {
              logger.log('[Desktop] Could not verify subscription status on disconnect');
              // Don't reset if we expect to auto-restart
              if (!wasLive) {
                setIsLive(false);
              }
            } else {
              logger.log('[Desktop] Subscription still active, will auto-restart on reconnect');
              // Keep isLive true - auto-restart will handle it
            }
          })
          .catch(() => {
            // Don't reset if we expect to auto-restart
            if (!wasLive) {
              setIsLive(false);
            }
          });
      }
    },
  });

  // Sync PTT setting to server when WebSocket connects (for session recovery)
  useEffect(() => {
    if (settingsLoaded && wsConnected && !pendingCaptureOnPttAckRef.current) {
      logger.log('[Desktop] Syncing PTT setting to server:', pttEnabled);
      sendMessage({ type: 'update_ptt', enabled: pttEnabled });
    }
  }, [pttEnabled, settingsLoaded, wsConnected, sendMessage]);

  useEffect(() => {
    if (!pttEnabled) {
      setPttDebug({
        turnPhase: 'idle',
        lastEvent: 'ptt_disabled',
        lastReason: null,
        lastTraceId: null,
        updatedAt: Date.now(),
      });
    }
  }, [pttEnabled]);

  // Auto-connect to WebSocket on mount or when token becomes available
  // This enables "Send Test Signal" and real-time status updates before Go Live
  useEffect(() => {
    if (wsUrl && !wsConnectedRef.current && !subscriptionRejected) {
      logger.log('[Desktop] Auto-connecting to WebSocket...');
      connect(wsUrl);
    }
  }, [wsUrl, connect, subscriptionRejected]);

  const handleTestOverlay = useCallback(() => {
    sendMessage({ type: 'test_overlay' });
    showToast('Sent test stats to overlay', 'success');
  }, [sendMessage, showToast]);



  const {
    isActive: isVisionActive,
    startCapture: startVisionCapture,
    stopCapture: stopVisionCapture,
    captureOneFrame,
  } = useVisionCapture({
    onFrame: (data) => {
      if (!visualsEnabled) {
        return;
      }

      // Direct call to the WebSocket send helper
      if (sendVision) {
        sendVision(data);
      } else {
        console.error('[Vision] sendVision not available');
      }
    },
    fps: 1,
    quality: 0.6,
    mode: 'on-demand',
  });

  // Keep refs for use in callbacks
  const captureOneFrameRef = useRef(captureOneFrame);
  useEffect(() => { captureOneFrameRef.current = captureOneFrame; }, [captureOneFrame]);
  const isVisionActiveRef = useRef(isVisionActive);
  useEffect(() => { isVisionActiveRef.current = isVisionActive; }, [isVisionActive]);
  const onDemandVisionRef = useRef(onDemandVision);
  useEffect(() => { onDemandVisionRef.current = onDemandVision; }, [onDemandVision]);




  const proceedWithVisionSetup = useCallback(async () => {
    setShowPrivacyWarning(false);
    if (window.electronAPI) {
      // Electron: Show custom picker
      setShowScreenPicker(true);
      setIsLoadingSources(true);
      try {
        const sources = await window.electronAPI.getDesktopSources();
        setDesktopSources(sources);
      } catch (error) {
        console.error('Failed to get desktop sources:', error);
        showToast('Failed to list screens', 'error');
        setShowScreenPicker(false);
      } finally {
        setIsLoadingSources(false);
      }
    } else {
      // Web: Use standard browser picker
      void startVisionCapture();
    }
  }, [startVisionCapture, showToast]);

  const handleStartVisionClick = useCallback(() => {
    const hasSeenWarning = localStorage.getItem('streambuddy_vision_privacy_warning_seen') === 'true';
    if (hasSeenWarning) {
      void proceedWithVisionSetup();
    } else {
      setShowPrivacyWarning(true);
    }
  }, [proceedWithVisionSetup]);

  const handleSourceSelected = useCallback((sourceId: string) => {
    setShowScreenPicker(false);
    void startVisionCapture(sourceId);
  }, [startVisionCapture]);

  // Sync WebSocket sendAudio ref

  useEffect(() => {
    sendAudioRef.current = sendAudio;
  }, [sendAudio]);

  const handleAudioData = useCallback((audioData: ArrayBuffer) => {
    // Debug loopback - hear what you're sending to the AI
    if (loopbackEnabled) {
      void loopback.playAudio(audioData);
    }
    if (wsConnectedRef.current && sendAudioRef.current) {
      sendAudioRef.current(audioData);
    }
  }, [loopbackEnabled, loopback]);

  const { isCapturing, audioLevel, startCapture, stopCapture, mute, unmute } = useAudioCapture({
    onAudioData: handleAudioData,
    pttEnabled,
    deviceId: selectedMicId || undefined,
    inputGain,
    trailingBufferMs: 200,
    noiseGateEnabled: pttEnabled,
  });

  useEffect(() => {
    sendEndAudioTurnRef.current = sendEndAudioTurn;
  }, [sendEndAudioTurn]);

  useEffect(() => {
    unmuteRef.current = unmute;
  }, [unmute]);

  // Sync refs for circular dependencies
  useEffect(() => {
    isCapturingRef.current = isCapturing;
    startCaptureRef.current = startCapture;
    stopCaptureRef.current = stopCapture;
  }, [isCapturing, startCapture, stopCapture]);

  const handleManualRestart = useCallback(async () => {
    logger.log('[Dashboard] Initiating hard restart - stopping audio first');
    // Stop capturing immediately to prevent stale audio
    await stopCapture();
    // Tell server to reset
    restartSession();
  }, [stopCapture, restartSession]);

  // Keep isLiveRef in sync with isLive state
  useEffect(() => {
    isLiveRef.current = isLive;
    if (!isLive && isCapturing) {
      void stopCapture();
    }
  }, [isLive, isCapturing, stopCapture]);

  const requestLiveSessionStart = useCallback(() => {
    if (sessionStartRequestedRef.current) {
      return;
    }

    if (!isLiveRef.current || !wsConnectedRef.current || !settingsLoadedRef.current) {
      return;
    }

    if (pendingCaptureOnPttAckRef.current) {
      logger.log('[Desktop] Waiting for PTT sync before starting AI session');
      return;
    }

    if (liveSessionStartRetriesExhaustedRef.current) {
      return;
    }

    const freshnessCutoff = Math.max(socketConnectedAtRef.current, liveStartRequestedAtRef.current);
    const hasFreshSessionStatus = sessionStatusUpdatedAtRef.current >= freshnessCutoff;

    if (hasFreshSessionStatus && sessionStatusRef.current?.connected === true) {
      logger.log('[Desktop] AI session already active on server, skipping startSession');
      liveSessionStartRetryCountRef.current = 0;
      liveSessionStartRetriesExhaustedRef.current = false;
      return;
    }

    if (liveSessionRetryTimeoutRef.current) {
      clearTimeout(liveSessionRetryTimeoutRef.current);
      liveSessionRetryTimeoutRef.current = null;
    }

    sessionStartRequestedRef.current = true;
    liveSessionStartRetriesExhaustedRef.current = false;
    logger.log('[Desktop] AI session not active, sending startSession');
    startSession();
  }, [startSession]);

  useEffect(() => {
    requestLiveSessionStartRef.current = requestLiveSessionStart;
    return () => {
      if (requestLiveSessionStartRef.current === requestLiveSessionStart) {
        requestLiveSessionStartRef.current = null;
      }
    };
  }, [requestLiveSessionStart]);

  useEffect(() => {
    if (isLive && wsConnected && settingsLoaded) {
      requestLiveSessionStart();
    }
  }, [isLive, wsConnected, settingsLoaded, sessionStatus?.connected, requestLiveSessionStart]);

  useEffect(() => {
    if (!isLive || !isGeminiConnected || isCapturing || pendingCaptureOnPttAckRef.current) {
      return;
    }

    logger.log('[Desktop] Gemini ready, starting audio capture');
    void startCapture().catch((error) => {
      console.error('[Desktop] Failed to start audio capture after Gemini became ready:', error);
    });
  }, [isLive, isGeminiConnected, isCapturing, startCapture]);

  // Push-to-talk keyboard handling
  const PTT_TAP_STOP_ONLY_MS = 180;
  const pttTurnEndDelayMs = 280;
  const pttPressStartedAtRef = useRef<number | null>(null);
  // Ref to track pending unmute while waiting for ptt_start_ack
  const pendingUnmuteRef = useRef(false);
  const isAiSpeakingRef = useRef(isAiSpeaking);
  useEffect(() => {
    isAiSpeakingRef.current = isAiSpeaking;
  }, [isAiSpeaking]);

  const handsFreeState = useMemo(() => {
    return deriveHandsFreeState(pttDebug.turnPhase, isAiThinking, isAiSpeaking);
  }, [isAiSpeaking, isAiThinking, pttDebug.turnPhase]);

  const openPttAudioTurn = useCallback((startTraceId: string) => {
    pttStartAckTraceRef.current = startTraceId;
    pttStartSentRef.current = true;
    sendPttStart(startTraceId);

    if (pttStartAckTimeoutRef.current) {
      clearTimeout(pttStartAckTimeoutRef.current);
    }
    pttStartAckTimeoutRef.current = setTimeout(() => {
      const timeoutTransition = transitionPttStartTimeout({
        pendingUnmute: pendingUnmuteRef.current,
        expectedTraceId: pttStartAckTraceRef.current,
      });
      pendingUnmuteRef.current = timeoutTransition.nextState.pendingUnmute;
      pttStartAckTraceRef.current = timeoutTransition.nextState.expectedTraceId;
      pttStartSentRef.current = false;
      clearPttStartAckWait();
      sendEndAudioTurn(`ptt-end-timeout-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);
      console.warn('[Desktop] PTT start acknowledgment timed out; keeping mic muted to preserve Gemini activity ordering');
    }, 300);

    pendingUnmuteRef.current = true;
  }, [clearPttStartAckWait, sendEndAudioTurn, sendPttStart]);

  const { isPressed: isPttActive } = usePushToTalk({
    hotkey: pttHotkey,
    enabled: pttEnabled && isLive,
    onStart: () => {
      const startTraceId = `ptt-start-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

      pttPressStartedAtRef.current = Date.now();
      const aiWasSpeaking = isAiSpeakingRef.current;
      if (aiWasSpeaking) {
        sendMessage({ type: 'interrupt' });
        delayedPttStartTimeoutRef.current = setTimeout(() => {
          delayedPttStartTimeoutRef.current = null;
          if (pttPressStartedAtRef.current !== null) {
            openPttAudioTurn(startTraceId);
          }
        }, PTT_TAP_STOP_ONLY_MS);
        return;
      }

      // Mark as pending - wait for ptt_start_ack before unmuting
      // This ensures activityStart is sent to Gemini BEFORE audio flows
      openPttAudioTurn(startTraceId);
    },
    onStop: () => {
      const heldMs = pttPressStartedAtRef.current ? Date.now() - pttPressStartedAtRef.current : 0;
      clearDelayedPttStartWait();

      if (!pendingUnmuteRef.current && !pttStartSentRef.current && heldMs < PTT_TAP_STOP_ONLY_MS) {
        pttPressStartedAtRef.current = null;
        return;
      }
      
      // If we're waiting for ptt_start_ack and user released, fail closed.
      // Audio never opened, so do not let a late ack unmute after release.
      if (pendingUnmuteRef.current) {
        pendingUnmuteRef.current = false;
        if (pttStartSentRef.current) {
          sendEndAudioTurn(`ptt-end-cancel-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`);
        }
        clearPttStartAckWait();
        pttPressStartedAtRef.current = null;
        return;
      }

      mute();
      const endTraceId = `ptt-end-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      pttEndAckTraceRef.current = endTraceId;
      // End turn immediately on release for low latency. Audio is still streamed
      // continuously in PTT listening mode, so delayed client-side turn end is unnecessary.
      sendEndAudioTurn(endTraceId);

      if (pttEndAckTimeoutRef.current) {
        clearTimeout(pttEndAckTimeoutRef.current);
      }
      pttEndAckTimeoutRef.current = setTimeout(() => {
        if (pttEndAckTraceRef.current === endTraceId) {
          sendEndAudioTurn(endTraceId);
          clearPttEndAckWait();
        }
      }, Math.min(300, pttTurnEndDelayMs));
      pttPressStartedAtRef.current = null;
    },
  });

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearDelayedPttStartWait();
      clearPttStartAckWait();
      clearPttEndAckWait();
    };
  }, [clearDelayedPttStartWait, clearPttEndAckWait, clearPttStartAckWait]);

  const pttState = useMemo(() => {
    return derivePttState(pttDebug.turnPhase, isAiThinking, isAiSpeaking, isPttActive);
  }, [isAiSpeaking, isAiThinking, isPttActive, pttDebug.turnPhase]);

  // Real-time subscription enforcement
  useEffect(() => {
    if (!isLive) return;

    const effectiveStatus = currentSubscriptionStatus || user?.subscriptionStatus;

    // Only enforce if we have a confirmed non-active status (not null/undefined)
    if (effectiveStatus && effectiveStatus !== 'active') {
      logger.log('[Desktop] Subscription no longer active, stopping stream');
      void stopCapture();
      disconnect();
      setIsLive(false);
      setShowUpgradeModal(true);
    }
  }, [currentSubscriptionStatus, isLive, user?.subscriptionStatus, stopCapture, disconnect]);

  const handleGoLive = async () => {
    if (isLive) {
      // Stop session immediately - cleanup happens in background on server
      sessionStartRequestedRef.current = false;
      liveStartRequestedAtRef.current = 0;
      liveSessionStartRetryCountRef.current = 0;
      liveSessionStartRetriesExhaustedRef.current = false;
      if (liveSessionRetryTimeoutRef.current) {
        clearTimeout(liveSessionRetryTimeoutRef.current);
        liveSessionRetryTimeoutRef.current = null;
      }
      stopSession();
      void stopCapture();
      stopVisionCapture();
      disconnect();
      setIsLive(false);
      localStorage.removeItem('streambuddy_was_live');
      hasConnectedBeforeRef.current = false; // Reset so next Go Live doesn't trigger auto-restart
    } else {
      // Fetch fresh subscription status before going live
      let canGoLive = false;
      try {
        const res = await fetch('/api/billing/subscription', { credentials: 'include' });
        if (res.ok) {
          const data = (await res.json()) as SubscriptionResponse;
          const status = parseSubscriptionStatus(data.status || data.subscriptionStatus);
          logger.log('[Desktop] Subscription check:', status);
          if (status) {
            setCurrentSubscriptionStatus(status);
          }
          if (status === 'active') {
            canGoLive = true;
          } else if (!status) {
            const cachedStatus = currentSubscriptionStatus || user?.subscriptionStatus;
            if (cachedStatus === 'active') {
              canGoLive = true;
            } else {
              setShowUpgradeModal(true);
              return;
            }
          } else {
            setShowUpgradeModal(true);
            return;
          }
        } else {
          // Can't verify subscription - fall back to cached status
          logger.log('[Desktop] Subscription API failed, using cached status');
          const cachedStatus = currentSubscriptionStatus || user?.subscriptionStatus;
          if (cachedStatus === 'active') {
            canGoLive = true;
          } else {
            setShowUpgradeModal(true);
            return;
          }
        }
      } catch (e) {
        console.error('[Desktop] Failed to verify subscription:', e);
        // Fall back to cached status
        const cachedStatus = currentSubscriptionStatus || user?.subscriptionStatus;
        if (cachedStatus === 'active') {
          canGoLive = true;
        } else {
          setShowUpgradeModal(true);
          return;
        }
      }

      if (canGoLive && wsUrl) {
        sessionStartRequestedRef.current = false;
        liveStartRequestedAtRef.current = Date.now();
        liveSessionStartRetryCountRef.current = 0;
        liveSessionStartRetriesExhaustedRef.current = false;
        if (liveSessionRetryTimeoutRef.current) {
          clearTimeout(liveSessionRetryTimeoutRef.current);
          liveSessionRetryTimeoutRef.current = null;
        }
        if (!wsConnectedRef.current) {
          connect(wsUrl);
        }
        setIsLive(true);
        setWentLiveAt(Date.now());
        localStorage.setItem('streambuddy_was_live', 'true');
        // Clear previous session's transcript and switch to activity view
        clearTranscript();
        setActiveSection('activity');
        // Vision is started manually by user for now to avoid permission surprises
      }
    }
  };

  // Sidebar navigation items configuration with sections
  type SidebarItem = { id: DashboardSection; label: string; icon: React.ReactNode; description: string };
  type SidebarSection = { header?: string; items: SidebarItem[] };

  const sidebarSections: SidebarSection[] = [{
    items: [
      {
        id: 'home',
        label: 'Home',
        icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10.75L12 3l9 7.75V20a1 1 0 01-1 1h-5.25v-6.25h-5.5V21H4a1 1 0 01-1-1v-9.25z" /></svg>,
        description: 'Overview'
      },
      ...(!quickStartHidden ? [{
        id: 'quickstart' as DashboardSection,
        label: 'Quick Start',
        icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M13 5l7 7-7 7" /></svg>,
        description: 'Setup'
      }] : []),
      ...(isLive ? [{
        id: 'activity' as DashboardSection,
        label: 'Live',
        icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12h3l2-7 3 14 2-7h3" /></svg>,
        description: 'Activity'
      }] : []),
      {
        id: 'ai-voice',
        label: 'AI Voice',
        icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18.5a6.5 6.5 0 006.5-6.5V8a6.5 6.5 0 10-13 0v4a6.5 6.5 0 006.5 6.5z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.5 12.5h.01M15.5 12.5h.01M9.5 15c1.55 1.05 3.45 1.05 5 0" /></svg>,
        description: 'Voice'
      },
      {
        id: 'ptt',
        label: 'Audio',
        icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18a3 3 0 003-3V6a3 3 0 10-6 0v9a3 3 0 003 3z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11v4a7 7 0 01-14 0v-4M12 22v-4" /></svg>,
        description: 'Mic'
      },
      {
        id: 'audio-bridge',
        label: 'Overlay',
        icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5.5A1.5 1.5 0 015.5 4h13A1.5 1.5 0 0120 5.5v9A1.5 1.5 0 0118.5 16h-13A1.5 1.5 0 014 14.5v-9zM8 20h8M10 16v4m4-4v4" /></svg>,
        description: 'Browser Source'
      },
      {
        id: 'viewer-memory',
        label: 'Memory',
        icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4h8l4 4v12H7a2 2 0 01-2-2V6a2 2 0 012-2z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 4v5h5M9 13h6M9 17h6" /></svg>,
        description: 'Context'
      },
      {
        id: 'chat-commands',
        label: 'Commands',
        icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h8M8 14h5M21 12c0 4.418-4.03 8-9 8a10.4 10.4 0 01-4.42-.98L3 20l1.42-3.78A7.17 7.17 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>,
        description: 'Chat'
      },
      {
        id: 'integrations',
        label: 'Integrations',
        icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l.672-.672M10.172 13.828a4 4 0 005.656 0l3-3a4 4 0 10-5.656-5.656l-.672.672" /></svg>,
        description: 'Services'
      },
      {
        id: 'settings',
        label: 'Settings',
        icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.7 1.7 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.7 1.7 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.7 1.7 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.7 1.7 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.7 1.7 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.7 1.7 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.7 1.7 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
        description: 'Filters'
      },
    ]
  }];

  const activeVoiceState = pttEnabled ? pttState : handsFreeState;
  const voiceStateLabel = sessionStatus?.muted
    ? 'Muted'
    : isLive
      ? activeVoiceState.label
      : 'Offline';
  const needsTwitchRelogin = isLive && (displayTwitchStatus === 'expired' || displayTwitchStatus === 'error');
  const chatConnectionLabel = !isLive
    ? 'Ready'
    : isReconnecting
      ? 'Reconnecting'
      : displayTwitchStatus === 'valid'
        ? 'Connected'
        : displayTwitchStatus === 'checking'
          ? 'Checking'
          : 'Relogin';
  const handleChatConnectionClick = useCallback(() => {
    if (needsTwitchRelogin) {
      window.location.href = `${serverUrl}/auth/twitch`;
    }
  }, [needsTwitchRelogin, serverUrl]);
  const isNebulaTheme = backgroundStyle === 'nebula';
  const shellClasses = isNebulaTheme
    ? {
      sidebar: 'w-[236px] h-full min-h-0 shrink-0 flex flex-col overflow-hidden rounded-[24px] border border-cyan-300/[0.12] bg-[linear-gradient(180deg,rgba(3,8,20,0.93),rgba(5,9,24,0.97))] p-3 shadow-[0_22px_80px_rgba(0,0,0,0.44)]',
      activeNav: 'border border-fuchsia-300/55 bg-[linear-gradient(110deg,rgba(37,99,235,0.42),rgba(92,45,202,0.34)_48%,rgba(190,24,171,0.36))] text-white shadow-[0_0_24px_rgba(59,130,246,0.20),0_0_30px_rgba(217,70,239,0.16)] ring-1 ring-inset ring-cyan-200/[0.12]',
      inactiveNav: 'border border-transparent text-slate-400 hover:text-slate-100 hover:border-cyan-300/[0.12] hover:bg-cyan-300/[0.045]',
      activeIcon: 'text-white',
      inactiveIcon: 'text-slate-500',
      proCard: 'flex w-full items-center gap-3 rounded-[18px] border border-emerald-300/[0.18] bg-[linear-gradient(135deg,rgba(6,78,59,0.34),rgba(6,23,36,0.84))] p-3 text-left transition-all hover:border-emerald-300/30 hover:bg-emerald-500/15',
      docsCard: 'flex w-full items-center justify-between rounded-[16px] border border-cyan-300/[0.10] bg-[#070d1c]/[0.78] px-3 py-2.5 text-left transition-all hover:border-cyan-300/25 hover:bg-cyan-300/[0.055]',
      main: 'relative min-w-0 h-full flex-1 overflow-y-auto custom-scrollbar rounded-[24px] border border-cyan-300/[0.12] bg-[linear-gradient(180deg,rgba(5,10,24,0.92),rgba(4,8,20,0.97))] p-4 shadow-[0_24px_90px_rgba(0,0,0,0.46)]',
    }
    : {
      sidebar: 'w-[236px] h-full min-h-0 shrink-0 flex flex-col overflow-hidden rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(17,22,38,0.88),rgba(14,17,31,0.94))] p-3 shadow-[0_20px_70px_rgba(5,9,24,0.34)]',
      activeNav: 'border border-fuchsia-400/30 bg-[linear-gradient(145deg,rgba(168,85,247,0.22),rgba(59,130,246,0.08),rgba(255,255,255,0.04))] text-white shadow-[0_14px_34px_rgba(124,58,237,0.18)] ring-1 ring-inset ring-white/10',
      inactiveNav: 'border border-transparent text-slate-300/78 hover:text-white hover:border-white/10 hover:bg-white/[0.055]',
      activeIcon: 'text-fuchsia-100',
      inactiveIcon: 'text-white/45',
      proCard: 'flex w-full items-center gap-3 rounded-[18px] border border-emerald-400/20 bg-emerald-500/10 p-3 text-left transition-all hover:border-emerald-300/30 hover:bg-emerald-500/15',
      docsCard: 'flex w-full items-center justify-between rounded-[16px] border border-white/10 bg-white/[0.03] px-3 py-2.5 text-left transition-all hover:border-cyan-300/20 hover:bg-white/[0.055]',
      main: 'relative min-w-0 h-full flex-1 overflow-y-auto custom-scrollbar rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(17,22,38,0.9),rgba(14,17,31,0.95))] p-4 shadow-[0_24px_80px_rgba(5,9,24,0.38)]',
    };
  return (
    <div className="h-full flex flex-col">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-xl shadow-lg transition-all ${toast.type === 'error'
          ? 'bg-red-500/90 text-white'
          : 'bg-green-500/90 text-white'
          }`}>
          {toast.message}
        </div>
      )}

      {/* Privacy Warning Modal */}
      {showPrivacyWarning && (
        <PrivacyWarningModal
          onContinue={() => { void proceedWithVisionSetup(); }}
          onCancel={() => setShowPrivacyWarning(false)}
        />
      )}

      {/* Regenerate Token Confirmation Modal */}
      {showRegenerateConfirm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#1a1a2e] border border-purple-500/30 rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div>
                <h2 className="text-white font-bold text-xl">Regenerate Token?</h2>
                <p className="text-gray-400 text-sm">Your current URL will stop working</p>
              </div>
            </div>

            <p className="text-gray-300 text-sm mb-6 leading-relaxed">
              This will create a new widget token and invalidate your current URL. You'll need to copy the new URL and update your browser source in OBS/Streamlabs.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowRegenerateConfirm(false)}
                className="flex-1 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-gray-300 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { void regenerateToken(); }}
                disabled={isRegenerating}
                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-sm font-bold transition-all disabled:opacity-50"
              >
                {isRegenerating ? 'Regenerating...' : 'Regenerate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Top Bar: Status + Go Live */}
      <div className="relative mb-3 shrink-0 overflow-hidden rounded-[24px] border border-white/10 bg-[linear-gradient(135deg,rgba(17,22,38,0.9),rgba(15,25,43,0.86))] p-4 shadow-[0_28px_80px_rgba(5,9,24,0.38)]">
        <div className="absolute inset-x-16 top-0 h-24 bg-gradient-to-r from-fuchsia-500/10 via-cyan-400/10 to-blue-500/10 blur-3xl" />
        <div className="relative flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border ${dashboardReadinessStyle.border} ${dashboardReadinessStyle.background}`}>
              <span className={`h-3 w-3 rounded-full ${dashboardReadinessStyle.dot}`} />
            </div>
            <div className="min-w-0">
              <div className={`text-sm font-black uppercase tracking-[0.16em] ${dashboardReadinessStyle.text}`}>
                {dashboardReadiness.title}
              </div>
              <div className="mt-1 truncate text-sm text-slate-300/70">
                {dashboardReadiness.description}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            {isReconnecting ? (
              <div className="font-black py-3.5 px-8 rounded-2xl text-sm bg-yellow-500/20 text-yellow-300 border border-yellow-500/30 flex items-center gap-3">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                RECONNECTING
              </div>
            ) : reconnectFailed ? (
              <button
                onClick={() => { void handleGoLive(); }}
                className="font-black py-3.5 px-8 rounded-2xl text-sm bg-red-500/20 text-red-300 border border-red-500/30 hover:bg-red-500/30 transition-colors"
              >
                RETRY CONNECTION
              </button>
            ) : !isLive && subscriptionExpired ? (
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="font-black py-3.5 px-8 rounded-2xl text-sm bg-gradient-to-r from-fuchsia-500 to-blue-500 text-white shadow-[0_18px_40px_rgba(124,58,237,0.35)]"
              >
                UPGRADE
              </button>
            ) : (
              <button
                onClick={() => { void handleGoLive(); }}
                className={`relative overflow-hidden font-black py-3.5 px-10 rounded-2xl text-sm transition-all duration-300 flex items-center gap-3 shadow-lg hover:scale-[1.02] active:scale-95 ${isLive
                  ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-red-500/25'
                  : 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-emerald-500/25'
                  }`}
              >
                <span className="relative flex h-2.5 w-2.5">
                  {isLive && wsConnected && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>}
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
                </span>
                {isLive ? (isCapturing ? 'STOP' : 'STOP') : 'GO LIVE'}
              </button>
            )}

            <button
              onClick={handleStartVisionClick}
              className={`relative flex h-12 w-12 items-center justify-center rounded-2xl border transition-all ${isVisionActive
                ? 'border-fuchsia-300/35 bg-fuchsia-500/20 text-white shadow-[0_0_28px_rgba(168,85,247,0.22)]'
                : 'border-white/10 bg-white/[0.055] text-slate-300 hover:bg-white/[0.085] hover:text-white'
                }`}
              title={isVisionActive ? 'Stop Vision' : 'Enable Vision'}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7z" />
              </svg>
            </button>

            {isLive && (
              <button
                onClick={() => { void handleManualRestart(); }}
                className="flex h-12 w-12 items-center justify-center rounded-2xl border border-purple-500/30 bg-purple-600/20 text-purple-200 transition-all hover:bg-purple-600/30"
                title="Restart AI session"
              >
                <svg className="w-4 h-4 transition-transform hover:rotate-180 duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Layout: Sidebar + Content */}
      <div className="flex-1 flex items-stretch gap-3 min-h-0 overflow-hidden">
        {/* Left Sidebar, Potentially add back blur here after black bg color using backdrop-blur-[2px] */}
        <div className={shellClasses.sidebar}>
          <nav className="space-y-2 overflow-visible">
            {sidebarSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="space-y-1.5">
                {section.header && (
                  <div className="px-3 pt-2 pb-1 first:pt-0">
                    <span className="text-[10px] font-black text-white/30 uppercase tracking-[0.24em]">
                      {section.header}
                    </span>
                  </div>
                )}
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSectionChange(item.id)}
                    className={`w-full relative overflow-hidden flex items-center gap-3 px-4 py-3 rounded-[16px] text-left transition-all ${activeSection === item.id || (item.id === 'settings' && (activeSection === 'profanity-filter' || activeSection === 'analytics'))
                      ? shellClasses.activeNav
                      : shellClasses.inactiveNav
                      }`}
                  >
                    <div className={`${activeSection === item.id || (item.id === 'settings' && (activeSection === 'profanity-filter' || activeSection === 'analytics')) ? shellClasses.activeIcon : shellClasses.inactiveIcon}`}>
                      {item.icon}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-[15px] truncate tracking-tight">{item.label}</div>
                    </div>
                  </button>
                ))}
              </div>
            ))}
          </nav>

          {/* Sidebar Footer: Subscription Status */}
          <div className="mt-auto pt-3 space-y-2">
            {(currentSubscriptionStatus || user?.subscriptionStatus) === 'active' ? (
              <a
                href={`${serverUrl}/api/billing/portal`}
                onClick={openBillingPortal}
                className={shellClasses.proCard}
              >
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_14px_rgba(52,211,153,0.55)]" />
                <div className="min-w-0">
                  <div className="text-[15px] font-black text-white">Pro Active</div>
                  <div className="mt-0.5 text-[11px] text-emerald-100/65">Manage Billing</div>
                </div>
              </a>
            ) : (
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="w-full rounded-[18px] border border-fuchsia-300/35 bg-[linear-gradient(145deg,rgba(88,28,135,0.32),rgba(15,23,42,0.88))] p-3 text-left transition-all duration-300 hover:-translate-y-0.5 hover:border-fuchsia-300/50"
              >
                <div className="text-[10px] font-black uppercase tracking-[0.22em] text-fuchsia-100/70">Unlock Stream Mode</div>
                <div className="mt-1.5 text-[15px] font-black text-white">Upgrade to Pro</div>
                <div className="mt-1 text-[11px] text-fuchsia-100/65">Voice control, vision AI, live reactions</div>
              </button>
            )}

            <button
              type="button"
              onClick={() => window.open('/guides', '_blank')}
              className={shellClasses.docsCard}
            >
              <div className="flex items-center gap-2.5">
                <svg className="h-4 w-4 text-slate-300/65" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 4h8l4 4v12H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M14 4v5h5" />
                </svg>
                <span className="text-[12px] font-bold text-slate-200/75">Docs & Guides</span>
              </div>
              <svg className="h-3.5 w-3.5 text-slate-300/45" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 17L17 7M9 7h8v8" />
              </svg>
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className={shellClasses.main}>
          {/* Home Section */}
          {activeSection === 'home' && (
            <HomeSection
              isLive={isLive}
              isOverlayConnected={isOverlayConnected}
              isGeminiConnected={isGeminiConnected}
              pttEnabled={pttEnabled}
              personaVoiceName={personaVoiceName}
              pttHotkeyDisplay={pttHotkeyDisplay}
              voiceStateLabel={voiceStateLabel}
              chatConnectionLabel={chatConnectionLabel}
              needsTwitchRelogin={needsTwitchRelogin}
              onChatConnectionClick={handleChatConnectionClick}
              quickStartHidden={quickStartHidden}
              onOpenQuickStart={showQuickStart}
              onToggleQuickStart={toggleQuickStart}
              onNavigate={handleSectionChange}
            />
          )}

          {/* Quick Start Section */}
          {activeSection === 'quickstart' && (
            <QuickStartSection
              onNavigate={handleSectionChange}
              isOverlayConnected={isOverlayConnected}
              hasStreamlabsToken={hasStreamlabsToken}
              hasVisitedPtt={hasVisitedPtt}
              hasVisitedAiVoice={hasVisitedAiVoice}
              isLive={isLive}
              onHideGuide={hideQuickStart}
            />
          )}

          {/* Analytics Section */}
          {activeSection === 'analytics' && <AnalyticsDashboard token={connectionToken || ''} serverUrl={serverUrl} />}

          {/* Activity Log Section */}
          {activeSection === 'activity' && (
            <ActivityLog assistantName={assistantName} isOverlayConnected={isOverlayConnected} />
          )}

          {/* Overlay Section */}
          {activeSection === 'audio-bridge' && (
            <AudioBridgeSection
              connectionToken={connectionToken}
              widgetToken={widgetToken}
              clientUrl={clientUrl}
              user={user}
              currentSubscriptionStatus={currentSubscriptionStatus}
              showUrl={showUrl}
              setShowUrl={setShowUrl}
              copyToClipboard={() => { void copyToClipboard(); }}
              regenerateToken={handleRegenerateClick}
              isRegenerating={isRegenerating}
              visualsEnabled={visualsEnabled}
              onVisualsEnabledChange={(enabled) => { void handleVisualsEnabledChange(enabled); }}
              overlayScale={overlayScale}
              onOverlayScaleChange={(scale) => { void handleOverlayScaleChange(scale); }}
              onTestOverlay={handleTestOverlay}
              gameStats={gameStats}
              isOverlayConnected={isOverlayConnected}
              onSubscribe={() => setShowUpgradeModal(true)}
            />
          )}

          {/* Integrations Section */}
          {activeSection === 'integrations' && (
            <div className="flex flex-col gap-3">
              <SectionHeader title="Integrations" description="Connect third-party services for alerts and events" />
              <IntegrationSettings serverUrl={serverUrl} onStreamlabsConfigured={handleStreamlabsConfigured} />
            </div>
          )}

          {/* Push to Talk Section */}
          {activeSection === 'ptt' && (
            <div className="space-y-4">
              <SectionHeader title="Audio Controls" description="Microphone setup, listening mode, and monitor controls" />
              <PushToTalkSettings
                pttHotkeyDisplay={pttHotkeyDisplay}
                onPttHotkeyChange={handlePttHotkeyChange}
                isLive={isLive}
                liveAudioLevel={audioLevel}
                isPttActive={isPttActive}
                selectedMicId={selectedMicId}
                onMicChange={handleMicChange}
                inputGain={inputGain}
                onInputGainChange={handleInputGainChange}
                loopbackEnabled={loopbackEnabled}
                onLoopbackEnabledChange={setLoopbackEnabled}
              />
            </div>
          )}

          {/* AI Voice Section */}
          {activeSection === 'ai-voice' && (
            <div className="space-y-4">
              <SectionHeader title="AI Voice Settings" description="Customize personality, voice, and behavior" />
              <Settings serverUrl={serverUrl} isLive={isLive} onNameChange={setAssistantName} onVoiceNameChange={setPersonaVoiceName} />
            </div>
          )}

          {/* Chat Commands Section */}
          {activeSection === 'chat-commands' && (
            <div className="space-y-4">
              <SectionHeader title="Commands" description="Control how chat can trigger StreamBuddy during stream." />
              <ChatCommandSettings
                enabled={allowViewerMessages}
                onEnabledChange={(enabled) => { void handleAllowViewerMessagesChange(enabled); }}
                prefix={commandPrefix}
                onPrefixChange={(prefix) => { void handleCommandPrefixChange(prefix); }}
                modOnly={modOnly}
                onModOnlyChange={(enabled) => { void handleModOnlyChange(enabled); }}
                cooldown={commandCooldown}
                onCooldownChange={(seconds) => { void handleCooldownChange(seconds); }}
                sendCooldownMessages={sendCooldownMessages}
                onSendCooldownMessagesChange={(send) => { void handleSendCooldownMessagesChange(send); }}
              />

              {/* Channel Points Section */}
              <div className="rounded-[28px] border border-white/10 bg-black/15 p-4">
                <ChannelPointsSettings
                  enabled={channelPointsEnabled}
                  onEnabledChange={(enabled) => { void handleChannelPointsEnabledChange(enabled); }}
                  rewardIds={channelPointsRewardIds}
                  onRewardIdsChange={(ids) => { void handleChannelPointsRewardIdsChange(ids); }}
                />
              </div>
            </div>
          )}

          {/* Memory Section */}
          {activeSection === 'viewer-memory' && (
            <div className="flex flex-col gap-3">
              <SectionHeader title="Memory" description="Choose what StreamBuddy can remember about you, your stream, and returning viewers." />
              <ViewerMemorySettings serverUrl={serverUrl} />
            </div>
          )}

          {/* Profanity Filter Section */}
          {activeSection === 'profanity-filter' && (
            <div className="space-y-4">
              <SectionHeader title="Profanity Filter" description="Filters chat commands and donations. Streamlabs only filters alerts (not chat) if configured." />
              <ProfanityFilterSettings serverUrl={serverUrl} />
            </div>
          )}

          {/* Settings Section */}
          {activeSection === 'settings' && (
            <SettingsHubSection
              backgroundStyle={backgroundStyle}
              setBackgroundStyle={setBackgroundStyle}
              quickStartHidden={quickStartHidden}
              showQuickStart={showQuickStart}
              onNavigate={handleSectionChange}
            />
          )}
        </div>
      </div>

      {/* Floating Volume Control */}
      <FloatingVolumeControl
        volume={outputVolume}
        onVolumeChange={handleVolumeChange}
        onMuteToggle={handleMuteToggle}
        isMuted={isMuted}
      />

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <UpgradeModal
          serverUrl={serverUrl}
          onClose={() => setShowUpgradeModal(false)}
          openBillingPortal={openBillingPortal}
        />
      )}

      {/* Screen Picker Modal */}
      {showScreenPicker && (
        <ScreenPickerModal
          sources={desktopSources}
          isLoading={isLoadingSources}
          onSelect={handleSourceSelected}
          onClose={() => setShowScreenPicker(false)}
        />
      )}
    </div>
  );
}

function HomeSection({
  isLive,
  isOverlayConnected,
  isGeminiConnected,
  pttEnabled,
  personaVoiceName,
  pttHotkeyDisplay,
  voiceStateLabel,
  chatConnectionLabel,
  needsTwitchRelogin,
  onChatConnectionClick,
  quickStartHidden,
  onOpenQuickStart,
  onToggleQuickStart,
  onNavigate,
}: {
  isLive: boolean;
  isOverlayConnected: boolean;
  isGeminiConnected: boolean;
  pttEnabled: boolean;
  personaVoiceName: string;
  pttHotkeyDisplay: string;
  voiceStateLabel: string;
  chatConnectionLabel: string;
  needsTwitchRelogin: boolean;
  onChatConnectionClick: () => void;
  quickStartHidden: boolean;
  onOpenQuickStart: () => void;
  onToggleQuickStart: () => void;
  onNavigate: (section: DashboardSection) => void;
}) {
  const setupCards = [
    { label: 'OBS Connection', value: isOverlayConnected ? 'Connected' : 'Open OBS / check URL', done: isOverlayConnected, section: 'audio-bridge' as DashboardSection },
    { label: 'Chat Connection', value: chatConnectionLabel, done: !needsTwitchRelogin, onClick: onChatConnectionClick },
    { label: 'Microphone', value: pttEnabled ? 'Push-to-talk' : 'Audio controls', done: true, section: 'ptt' as DashboardSection },
    { label: 'AI Audio', value: isGeminiConnected || isLive ? 'Active' : 'Ready', done: isGeminiConnected || !isLive, section: 'ai-voice' as DashboardSection },
  ];

  const liveStatus = [
    { label: 'StreamBuddy', value: isLive ? voiceStateLabel : 'Ready', ready: true, section: 'ai-voice' as DashboardSection },
    { label: 'OBS Browser Source', value: isOverlayConnected ? 'Connected' : 'Open OBS / check URL', ready: isOverlayConnected, section: 'audio-bridge' as DashboardSection },
    { label: 'Chat Connection', value: chatConnectionLabel, ready: !needsTwitchRelogin, onClick: onChatConnectionClick },
    { label: 'Voice Engine', value: isGeminiConnected || !isLive ? 'Ready' : 'Starting', ready: true, section: 'ai-voice' as DashboardSection },
    { label: 'Memory', value: 'Learning', ready: true, section: 'viewer-memory' as DashboardSection },
    { label: 'Commands', value: 'Available', ready: true, section: 'chat-commands' as DashboardSection },
  ];

  return (
    <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_300px]">
      <div className="space-y-3">
        <section className="relative overflow-hidden rounded-[26px] border border-white/10 bg-[linear-gradient(135deg,rgba(15,23,42,0.94),rgba(13,20,36,0.94))] p-5 shadow-[0_28px_90px_rgba(4,8,22,0.38)]">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(168,85,247,0.16),transparent_32%),radial-gradient(circle_at_76%_50%,rgba(34,211,238,0.13),transparent_34%)]" />
          <div className="relative">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-2xl font-black tracking-[-0.035em] text-white">
                  {isLive ? 'StreamBuddy is live' : isOverlayConnected ? 'StreamBuddy is ready' : 'Connect the OBS overlay'}
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-300/72">
                  {isOverlayConnected
                    ? 'Your AI co-host is standing by to speak when it matters.'
                    : 'Open OBS or make sure the Browser Source URL is correct before going live.'}
                </p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-[190px_minmax(0,1fr)] gap-3">
              <button
                type="button"
                onClick={() => onNavigate('ai-voice')}
                className="rounded-[20px] border border-white/10 bg-black/24 p-3 text-left transition-all hover:border-fuchsia-300/25 hover:bg-white/[0.055]"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full border border-emerald-300/40 bg-emerald-400/10 shadow-[0_0_32px_rgba(52,211,153,0.18)]">
                    <svg className="h-8 w-8 text-fuchsia-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 18.5a6.5 6.5 0 006.5-6.5V8a6.5 6.5 0 10-13 0v4a6.5 6.5 0 006.5 6.5z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8.5 12.5h.01M15.5 12.5h.01M9.5 15c1.55 1.05 3.45 1.05 5 0" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-sm font-black text-white">{personaVoiceName}</div>
                    <div className="text-xs text-slate-300/62">Voice Profile</div>
                    <div className="mt-2 inline-flex rounded-lg border border-white/10 bg-white/[0.055] px-2 py-1 text-[10px] font-black text-slate-200">Change</div>
                  </div>
                </div>
              </button>

              <div className="relative overflow-hidden rounded-[20px] border border-white/10 bg-black/16 px-5 py-3">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_42%_50%,rgba(34,211,238,0.11),transparent_36%),radial-gradient(circle_at_68%_50%,rgba(168,85,247,0.1),transparent_34%)]" />
                <div className="relative flex min-h-[74px] items-center gap-4">
                  <div className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full border ${isOverlayConnected ? 'border-emerald-300/30 bg-emerald-400/10' : 'border-amber-300/25 bg-amber-400/[0.08]'}`}>
                    <span className={`h-2.5 w-2.5 rounded-full ${isOverlayConnected ? 'bg-emerald-300 shadow-[0_0_16px_rgba(52,211,153,0.75)]' : 'bg-amber-300 shadow-[0_0_14px_rgba(251,191,36,0.6)]'}`} />
                    <span className={`absolute inset-1.5 rounded-full border ${isOverlayConnected ? 'border-emerald-200/10' : 'border-amber-200/10'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-black text-white">{isOverlayConnected ? 'StreamBuddy is ready' : 'Connect the OBS overlay'}</div>
                    <div className="mt-1 text-xs text-slate-300/62">
                      {isOverlayConnected ? `${personaVoiceName} is standing by for your stream.` : 'Your selected voice is ready once OBS connects.'}
                    </div>
                  </div>
                  <div className="shrink-0 rounded-full border border-fuchsia-300/20 bg-fuchsia-400/[0.08] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-fuchsia-200">
                    Push-to-Talk
                  </div>
                </div>
              </div>
            </div>

            {pttEnabled && (
              <button
                type="button"
                onClick={() => onNavigate('ptt')}
                className="mt-3 flex w-full items-center justify-between rounded-[18px] border border-white/10 bg-white/[0.04] px-4 py-3 text-left transition-all hover:border-fuchsia-300/25 hover:bg-white/[0.065]"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-fuchsia-500/20 text-fuchsia-100">
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 18a3 3 0 003-3V6a3 3 0 10-6 0v9a3 3 0 003 3z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 11v4a7 7 0 01-14 0v-4M12 22v-4" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-black text-white">Test StreamBuddy</div>
                    <div className="text-xs text-slate-300/62">Hold <span className="font-black text-fuchsia-200">{pttHotkeyDisplay}</span> while live to talk to StreamBuddy</div>
                  </div>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.055] px-3 py-1.5 text-xs font-black text-white">{pttHotkeyDisplay}</div>
              </button>
            )}

            <div className="mt-5 border-t border-white/10 pt-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-black tracking-tight text-white">Stream Setup</h3>
                <button
                  type="button"
                  onClick={onToggleQuickStart}
                  className="text-xs font-bold text-cyan-200/70 transition-colors hover:text-cyan-100"
                >
                  {quickStartHidden ? 'Show Quick Start' : 'Hide Quick Start'}
                </button>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 lg:grid-cols-4">
                {setupCards.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => item.onClick ? item.onClick() : item.section ? onNavigate(item.section) : undefined}
                    disabled={!item.onClick && !item.section}
                    className="rounded-[18px] border border-white/10 bg-black/20 px-3 py-3 text-left transition-all hover:border-cyan-300/25 hover:bg-white/[0.055]"
                  >
                    <div className="flex items-center gap-2">
                      <div className={`flex h-7 w-7 items-center justify-center rounded-full border ${item.done ? 'border-emerald-300/40 bg-emerald-400/10 text-emerald-300' : 'border-white/12 bg-white/[0.04] text-slate-300/70'}`}>
                        {item.done ? (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <span className="h-1.5 w-1.5 rounded-full bg-slate-300/70" />
                        )}
                      </div>
                      <div>
                        <div className="text-xs font-black text-white">{item.label}</div>
                        <div className="mt-0.5 text-[11px] text-slate-300/62">{item.value}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden rounded-[26px] border border-white/10 bg-[linear-gradient(135deg,rgba(15,23,42,0.92),rgba(12,21,38,0.94))] p-5">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_86%_40%,rgba(34,211,238,0.1),transparent_30%)]" />
          <div className="relative">
            <h3 className="text-lg font-black tracking-tight text-white">What your stream will hear</h3>
            <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
              <button
                type="button"
                onClick={() => onNavigate('audio-bridge')}
                className="rounded-[20px] border border-white/10 bg-black/22 p-4 text-left transition-all hover:border-fuchsia-300/25 hover:bg-white/[0.055]"
              >
                <div className="text-xs text-slate-300/62">Overlay</div>
                <div className="mt-3 rounded-[18px] border border-white/10 bg-[linear-gradient(135deg,rgba(88,28,135,0.35),rgba(15,23,42,0.9))] p-4">
                  <div className="flex items-center gap-3 rounded-2xl bg-black/28 px-4 py-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full border border-fuchsia-300/30 bg-fuchsia-500/14 text-fuchsia-100">
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 18.5a6.5 6.5 0 006.5-6.5V8a6.5 6.5 0 10-13 0v4a6.5 6.5 0 006.5 6.5z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-black text-white">StreamBuddy</div>
                      <div className="text-xs text-slate-300/62">{isLive ? 'Speaking when needed' : 'Ready for OBS'}</div>
                    </div>
                    <div className="ml-auto flex gap-1">
                      {Array.from({ length: 8 }).map((_, index) => (
                        <span key={index} className="h-1 w-1 rounded-full bg-fuchsia-300/70" />
                      ))}
                    </div>
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => onNavigate('ai-voice')}
                className="rounded-[20px] border border-white/10 bg-black/22 p-4 text-left transition-all hover:border-cyan-300/25 hover:bg-white/[0.055]"
              >
                <div className="text-xs text-slate-300/62">Voice Profile</div>
                <div className="mt-3 flex items-center gap-3 rounded-[18px] border border-white/10 bg-white/[0.04] px-4 py-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-fuchsia-300/30 bg-fuchsia-500/14 text-fuchsia-100">
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 18.5a6.5 6.5 0 006.5-6.5V8a6.5 6.5 0 10-13 0v4a6.5 6.5 0 006.5 6.5z" />
                    </svg>
                  </div>
                  <div>
                    <div className="text-sm font-black text-white">{personaVoiceName}</div>
                    <div className="text-xs text-slate-300/62">Current StreamBuddy voice</div>
                  </div>
                  <div className="ml-auto text-cyan-200/70">→</div>
                </div>
                <div className="mt-4 border-t border-white/8 pt-3 text-xs text-slate-300/62">Try different voices from AI Voice.</div>
              </button>
            </div>
          </div>
        </section>
      </div>

      <aside>
        <section className="rounded-[24px] border border-white/10 bg-[linear-gradient(135deg,rgba(15,23,42,0.92),rgba(16,17,36,0.94))] p-4">
          <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-1">
            <div>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-black text-white">Live Status</h3>
                <span className={`rounded-full border px-3 py-1 text-xs font-black ${isOverlayConnected ? 'border-emerald-300/20 bg-emerald-400/12 text-emerald-300' : 'border-amber-300/40 bg-amber-400/10 text-amber-200'}`}>
                  {isOverlayConnected ? 'Ready' : 'Needs OBS'}
                </span>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                {liveStatus.map((status) => (
                  <button
                    key={status.label}
                    type="button"
                    onClick={() => status.onClick ? status.onClick() : status.section ? onNavigate(status.section) : undefined}
                    disabled={!status.onClick && !status.section}
                    className="flex w-full items-center gap-3 rounded-2xl px-2 py-1.5 text-left transition-colors hover:bg-white/[0.045]"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/[0.045] text-slate-200">
                      <span className={`h-2 w-2 rounded-full ${status.ready ? 'bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.6)]' : 'bg-slate-400/70'}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-bold text-white">{status.label}</div>
                      <div className="text-xs text-slate-300/58">{status.value}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-white/10 pt-4 lg:border-l lg:border-t-0 lg:pl-4 lg:pt-0 2xl:border-l-0 2xl:border-t 2xl:pl-0 2xl:pt-4">
              <h3 className="text-lg font-black text-white">Next</h3>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                {[
                  { label: 'Setup Guide', value: 'Step-by-step instructions', section: 'quickstart' as DashboardSection },
                  { label: 'Memory', value: 'Viewer context and recall', section: 'viewer-memory' as DashboardSection },
                  { label: 'Analytics', value: 'Session performance', section: 'analytics' as DashboardSection },
                ].map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => item.section === 'quickstart' ? onOpenQuickStart() : onNavigate(item.section)}
                    className="flex w-full items-center justify-between rounded-2xl border border-white/10 bg-black/18 px-4 py-3 text-left transition-all hover:border-fuchsia-300/25 hover:bg-white/[0.055]"
                  >
                    <div>
                      <div className="text-sm font-black text-white">{item.label}</div>
                      <div className="mt-0.5 text-xs text-slate-300/58">{item.value}</div>
                    </div>
                    <span className="text-cyan-200/60">→</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      </aside>
    </div>
  );
}

function SettingsHubSection({
  backgroundStyle,
  setBackgroundStyle,
  quickStartHidden,
  showQuickStart,
  onNavigate,
}: {
  backgroundStyle: BackgroundStyle;
  setBackgroundStyle: (style: BackgroundStyle) => void;
  quickStartHidden: boolean;
  showQuickStart: () => void;
  onNavigate: (section: DashboardSection) => void;
}) {
  const backgroundOptions: { id: BackgroundStyle; label: string }[] = [
    { id: 'nebula', label: 'Nebula' },
    { id: 'cosmic', label: 'Cosmic' },
    { id: 'stars', label: 'Classic' },
  ];

  return (
    <div className="space-y-4">
      <SectionHeader title="Settings" description="App preferences, safety controls, and setup visibility." />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <section className="rounded-[24px] border border-white/10 bg-white/[0.035] p-5">
          <h3 className="text-lg font-black text-white">Background atmosphere</h3>
          <p className="mt-1 text-sm text-slate-300/62">Choose the desktop app background style.</p>
          <div className="mt-4 grid grid-cols-3 gap-2 rounded-[20px] border border-white/10 bg-black/24 p-1.5">
            {backgroundOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setBackgroundStyle(option.id)}
                className={`rounded-2xl px-3 py-3 text-[10px] font-black uppercase tracking-[0.18em] transition-all ${backgroundStyle === option.id
                  ? 'bg-[linear-gradient(135deg,#c026d3,#2563eb)] text-white shadow-[0_14px_32px_rgba(124,58,237,0.32)]'
                  : 'text-white/45 hover:bg-white/[0.06] hover:text-white/80'
                  }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-[24px] border border-white/10 bg-white/[0.035] p-5">
          <h3 className="text-lg font-black text-white">Safety and setup</h3>
          <p className="mt-1 text-sm text-slate-300/62">Keep moderation and onboarding within reach.</p>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => onNavigate('profanity-filter')}
              className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-left transition-all hover:border-fuchsia-300/25 hover:bg-white/[0.055]"
            >
              <div className="text-sm font-black text-white">Profanity Filter</div>
              <div className="mt-1 text-xs text-slate-300/58">Chat command and alert filtering</div>
            </button>
            {quickStartHidden ? (
              <button
                type="button"
                onClick={showQuickStart}
                className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-left transition-all hover:border-cyan-300/25 hover:bg-white/[0.055]"
              >
                <div className="text-sm font-black text-white">Show Quick Start</div>
                <div className="mt-1 text-xs text-slate-300/58">Bring setup guide back</div>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onNavigate('quickstart')}
                className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-left transition-all hover:border-cyan-300/25 hover:bg-white/[0.055]"
              >
                <div className="text-sm font-black text-white">Open Quick Start</div>
                <div className="mt-1 text-xs text-slate-300/58">Review setup progress</div>
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

// Section Header Component
function SectionHeader({ title, description }: { title: string; description: string }) {
  return (
    <div className="relative overflow-hidden rounded-[26px] border border-white/10 bg-[linear-gradient(145deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] px-6 py-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
      <div className="absolute inset-y-0 left-0 w-1 rounded-r-full bg-gradient-to-b from-fuchsia-400 via-violet-300 to-cyan-300" />
      <div className="text-[10px] font-black uppercase tracking-[0.26em] text-white/35">Workspace</div>
      <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-white">{title}</h2>
      <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-300/70">{description}</p>
    </div>
  );
}

// Quick Start Section
function QuickStartSection({
  onNavigate,
  isOverlayConnected,
  hasStreamlabsToken,
  hasVisitedPtt,
  hasVisitedAiVoice,
  isLive,
  onHideGuide,
}: {
  onNavigate: (section: DashboardSection) => void;
  isOverlayConnected: boolean;
  hasStreamlabsToken: boolean;
  hasVisitedPtt: boolean;
  hasVisitedAiVoice: boolean;
  isLive: boolean;
  onHideGuide: () => void;
}) {
  const steps = [
    {
      num: 1,
      title: 'Overlay',
      desc: 'Copy your source URL and add it as a Browser Source in OBS.',
      detail: 'This is what lets StreamBuddy audio reach your stream.',
      section: 'audio-bridge' as DashboardSection,
      done: isOverlayConnected
    },
    {
      num: 2,
      title: 'AI Voice',
      desc: 'Customize the voice, accent, pacing, and personality style.',
      detail: 'Tune how StreamBuddy sounds before you bring it live.',
      section: 'ai-voice' as DashboardSection,
      done: hasVisitedAiVoice
    },
    {
      num: 3,
      title: 'Audio Controls',
      desc: 'Select your mic and configure the push-to-talk hotkey.',
      detail: 'Set the input and talk behavior you want while streaming.',
      section: 'ptt' as DashboardSection,
      done: hasVisitedPtt
    },
    {
      num: 4,
      title: 'Integrations',
      desc: 'Connect Streamlabs for donation and subscriber alerts.',
      detail: 'StreamBuddy uses those events for live reactions.',
      section: 'integrations' as DashboardSection,
      done: hasStreamlabsToken
    },
  ];
  const completeCount = steps.filter(step => step.done).length;
  const nextStep = steps.find(step => !step.done) ?? steps[0];

  return (
    <div className="space-y-3">
      <section className="relative overflow-hidden rounded-[26px] border border-white/10 bg-[linear-gradient(135deg,rgba(15,23,42,0.94),rgba(18,16,39,0.92))] p-5 shadow-[0_24px_80px_rgba(5,9,24,0.34)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_16%_0%,rgba(168,85,247,0.18),transparent_34%),radial-gradient(circle_at_86%_42%,rgba(34,211,238,0.12),transparent_30%)]" />
        <div className="relative flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-fuchsia-300/20 bg-fuchsia-500/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.24em] text-fuchsia-100/80">
              Quick Start
            </div>
            <h2 className="mt-3 text-3xl font-black tracking-[-0.04em] text-white">Get StreamBuddy ready in minutes</h2>
            <p className="mt-2 text-sm leading-6 text-slate-300/72">
              Finish the essentials, then go live when your voice, audio, and event sources feel right.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="rounded-[18px] border border-white/10 bg-black/22 px-4 py-3">
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-white/35">Progress</div>
              <div className="mt-1 text-xl font-black text-white">{completeCount}/{steps.length}</div>
            </div>
            <button
              type="button"
              onClick={() => onNavigate(nextStep.section)}
              className="rounded-[18px] border border-cyan-300/20 bg-cyan-400/10 px-4 py-3 text-left transition-all hover:border-cyan-200/35 hover:bg-cyan-300/14"
            >
              <div className="text-[10px] font-black uppercase tracking-[0.22em] text-cyan-100/65">Next</div>
              <div className="mt-1 text-sm font-black text-white">{nextStep.title}</div>
            </button>
            <button
              type="button"
              onClick={onHideGuide}
              className="rounded-[18px] border border-white/10 bg-white/[0.04] px-4 py-3 text-[10px] font-black uppercase tracking-[0.22em] text-white/58 transition-all hover:border-white/15 hover:bg-white/[0.07] hover:text-white"
            >
              Hide
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-[26px] border border-white/10 bg-black/18 p-3">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-5">
          {steps.map((step) => (
            <button
              key={step.num}
              type="button"
              onClick={() => onNavigate(step.section)}
              className={`group min-h-[182px] rounded-[22px] border p-4 text-left transition-all hover:-translate-y-0.5 ${step.done
                ? 'border-emerald-300/20 bg-[linear-gradient(145deg,rgba(16,185,129,0.13),rgba(15,23,42,0.52))] shadow-[0_18px_46px_rgba(16,185,129,0.08)]'
                : 'border-white/10 bg-[linear-gradient(145deg,rgba(255,255,255,0.055),rgba(255,255,255,0.02))] hover:border-fuchsia-300/24 hover:bg-white/[0.065]'
                }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-2xl border text-sm font-black ${step.done
                  ? 'border-emerald-300/35 bg-emerald-400/12 text-emerald-200'
                  : 'border-white/12 bg-black/24 text-slate-200'
                  }`}>
                  {step.done ? (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    step.num
                  )}
                </div>
                <span className={`rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.18em] ${step.done
                  ? 'border-emerald-300/20 bg-emerald-400/10 text-emerald-200/75'
                  : 'border-white/10 bg-white/[0.04] text-white/42'
                  }`}>
                  {step.done ? 'Ready' : 'Set up'}
                </span>
              </div>
              <h3 className="mt-4 text-base font-black tracking-[-0.02em] text-white">{step.title}</h3>
              <p className="mt-2 text-xs font-bold leading-5 text-slate-200/78">{step.desc}</p>
              <p className="mt-1 min-h-[34px] text-[11px] leading-5 text-slate-300/52">{step.detail}</p>
              <div className="mt-3 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-cyan-100/58">
                Open
                <svg className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_280px]">
        <div className="rounded-[24px] border border-white/10 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(168,85,247,0.08),rgba(255,255,255,0.02))] p-4">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-white/35">Control</div>
          <h3 className="mt-2 text-lg font-black text-white">Start live only when it feels right</h3>
          <p className="mt-1 text-sm leading-6 text-slate-300/68">
            Push-to-talk, voice style, alert sources, and optional screen context can all be adjusted after setup.
          </p>
        </div>
        <div className="rounded-[24px] border border-white/10 bg-black/22 p-4">
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-white/35">Live State</div>
          <div className={`mt-3 inline-flex rounded-full border px-3 py-1 text-xs font-black ${isLive
            ? 'border-emerald-300/24 bg-emerald-400/12 text-emerald-200'
            : isOverlayConnected
              ? 'border-fuchsia-300/22 bg-fuchsia-500/10 text-fuchsia-100/72'
              : 'border-amber-300/40 bg-amber-400/10 text-amber-200'
            }`}>
            {isLive
              ? 'Currently live'
              : isOverlayConnected
                ? 'Ready when you are'
                : 'Connect OBS overlay'}
          </div>
        </div>
      </section>
    </div>
  );
}

// Overlay Section
interface AudioBridgeSectionProps {
  connectionToken: string | null;
  widgetToken: string | null;
  clientUrl: string;
  user: User;
  currentSubscriptionStatus: string | null;
  showUrl: boolean;
  setShowUrl: (show: boolean) => void;
  copyToClipboard: () => void;
  regenerateToken: () => void;
  isRegenerating: boolean;
  visualsEnabled: boolean;
  onVisualsEnabledChange: (enabled: boolean) => void;
  overlayScale: number;
  onOverlayScaleChange: (scale: number) => void;
  onTestOverlay: () => void;
  gameStats: Record<string, GameStat>;
  isOverlayConnected: boolean;
}

function AudioBridgeSection({
  connectionToken,
  widgetToken,
  clientUrl,
  user,
  currentSubscriptionStatus,
  showUrl,
  setShowUrl,
  copyToClipboard,
  regenerateToken,
  isRegenerating,
  visualsEnabled,
  onVisualsEnabledChange,
  overlayScale,
  onOverlayScaleChange,
  onTestOverlay,
  gameStats,
  isOverlayConnected,
  onSubscribe
}: AudioBridgeSectionProps & { onSubscribe: () => void }) {
  const isActive = currentSubscriptionStatus === 'active' || user.subscriptionStatus === 'active';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-black text-white uppercase tracking-tight">Overlay</h2>
          <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${isOverlayConnected ? 'bg-green-500/10 border-green-500/20' : 'bg-amber-500/10 border-amber-300/40'}`}>
            <span className={`w-1 h-1 rounded-full ${isOverlayConnected ? 'bg-green-500 animate-pulse' : 'bg-amber-300'}`} />
            <span className={`text-[9px] font-black uppercase tracking-widest leading-none ${isOverlayConnected ? 'text-green-500' : 'text-amber-300'}`}>
              {isOverlayConnected ? 'Connected' : 'Waiting for OBS'}
            </span>
          </div>
        </div>
        <p className="hidden md:block text-[11px] text-gray-500 font-medium italic">Secure endpoint for broadcasting software</p>
      </div>

      {!connectionToken ? (
        <div className="text-center py-12 bg-black/20 rounded-3xl border border-white/5">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mb-4"></div>
          <p className="text-gray-400 text-sm font-medium">Initializing secure endpoint...</p>
        </div>
      ) : !isActive ? (
        <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/30 p-8 rounded-3xl flex items-center gap-6">
          <div className="w-16 h-16 rounded-2xl bg-purple-500/20 flex items-center justify-center text-3xl border border-purple-500/30">
            ✨
          </div>
          <div className="flex-1">
            <h4 className="text-purple-300 font-black text-lg uppercase tracking-wide mb-1">Unlock Pro Features</h4>
            <p className="text-purple-200/60 mb-4">Start streaming with your AI co-host. Full voice control, vision AI, and chat engagement.</p>
            <button
              onClick={onSubscribe}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-black py-3 px-8 rounded-xl transition-all duration-300 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-105 flex items-center gap-2"
            >
              <span>Start Streaming</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Main Controls & URL Section Combined */}
          <div className="bg-black/20 border border-white/5 rounded-3xl overflow-hidden shadow-2xl">
            {/* Overlay Configuration Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/5 border-b border-white/5 bg-white/[0.02]">
              {/* Resolution / Canvas */}
              <div className="p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-500/10 flex items-center justify-center text-lg border border-blue-500/20">
                    📺
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-[10px] uppercase tracking-wider opacity-60">Canvas Resolution</h3>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-sm font-black text-white font-mono">1600<span className="text-gray-600 mx-0.5">x</span>240</span>
                      <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-[7px] font-black text-blue-400 uppercase">Recommended</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-green-500/5 border border-green-500/10">
                  <div className="w-1 h-1 rounded-full bg-green-500" />
                  <span className="text-[8px] text-green-500/80 font-black uppercase tracking-widest">Active</span>
                </div>
              </div>

              {/* Visuals & Scale */}
              <div className="p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-lg transition-colors ${visualsEnabled ? 'bg-purple-500/10 border-purple-500/20' : 'bg-gray-800/20 border-white/5'}`}>
                    ✨
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-[10px] uppercase tracking-wider opacity-60">Visual Overlay</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <button
                        onClick={() => onVisualsEnabledChange(!visualsEnabled)}
                        className={`relative inline-flex items-center h-4 w-8 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${visualsEnabled ? 'bg-purple-600' : 'bg-gray-700'}`}
                      >
                        <span className={`pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${visualsEnabled ? 'translate-x-4' : 'translate-x-1'}`} />
                      </button>
                      {visualsEnabled && (
                        <div className="flex items-center gap-1.5 bg-black/40 px-1.5 py-0.5 rounded-lg border border-white/5">
                          <button onClick={() => onOverlayScaleChange(Math.max(0.5, overlayScale - 0.1))} className="text-white/40 hover:text-white font-black text-xs px-1">−</button>
                          <span className="text-[10px] font-black text-purple-400 font-mono w-5 text-center">{overlayScale.toFixed(1)}</span>
                          <button onClick={() => onOverlayScaleChange(Math.min(5.0, overlayScale + 0.1))} className="text-white/40 hover:text-white font-black text-xs px-1">+</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <span className="text-[9px] text-gray-500 font-medium max-w-[80px] text-right leading-tight">Increase scale to sharpen text</span>
              </div>
            </div>

            {/* URL Section */}
            <div className="p-5 space-y-3">
              <div className="flex flex-col lg:flex-row gap-3">
                <div className="flex-1 bg-black/40 rounded-2xl border border-white/5 p-3 font-mono text-sm text-gray-400 relative overflow-hidden group flex items-center min-h-[52px]">
                  <div className={`${showUrl ? '' : 'blur-md select-none opacity-20'} transition-all duration-500 break-all w-full text-xs`}>
                    {`${clientUrl}/overlay?widgetToken=${widgetToken || '••••••••••••••••'}`}
                  </div>
                  {!showUrl && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="bg-[#0f0f16]/60 backdrop-blur-md px-3 py-1.5 rounded-full border border-purple-500/30 flex items-center gap-2 shadow-2xl">
                        <svg className="w-3 h-3 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                        <span className="text-[10px] font-black text-white/90 uppercase tracking-widest whitespace-nowrap">Source Hidden</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => setShowUrl(!showUrl)}
                    className="flex-1 lg:flex-none px-6 py-2 rounded-xl bg-white/[0.05] hover:bg-white/[0.08] text-white text-[10px] font-black uppercase tracking-widest transition-all border border-white/10 active:scale-95"
                  >
                    {showUrl ? 'Hide' : 'Reveal'}
                  </button>
                  <button
                    onClick={copyToClipboard}
                    className="flex-1 lg:flex-none px-6 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20 active:scale-95"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    Copy
                  </button>
                  <button
onClick={() => { void regenerateToken(); }}
                    disabled={isRegenerating}
                    className="p-3 rounded-xl bg-white/5 hover:bg-red-500/10 text-gray-400 hover:text-red-400 transition-all border border-white/10 hover:border-red-500/20 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 px-2">
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest">Connect:</span>
                  <p className="text-[10px] text-gray-500 font-medium">Add this as a <strong>Browser Source</strong> in OBS. Must be active for AI audio.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Setup + Preview Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Minimalist Instructions */}
            <div className="lg:col-span-4 space-y-2">
              <div className="px-1 mb-2">
                <span className="text-[9px] font-black text-gray-500 uppercase tracking-[0.2em]">Setup Steps</span>
              </div>

              {[
                { id: 1, title: 'Add Browser Source', desc: 'Use the URL from above' },
                { id: 2, title: 'Input Size', desc: '1600x240 recommended' },
                { id: 3, title: 'Control Audio', desc: 'Control volume in StreamBuddy' }
              ].map((step) => (
                <div key={step.id} className="bg-black/20 border border-white/5 rounded-2xl p-3 flex items-center gap-3 group hover:bg-white/[0.02] transition-colors">
                  <div className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-[10px] font-black text-gray-400 group-hover:text-purple-400 group-hover:border-purple-500/30 transition-colors">
                    0{step.id}
                  </div>
                  <div>
                    <h4 className="text-[11px] font-bold text-white leading-none">{step.title}</h4>
                    <p className="text-[10px] text-gray-500 mt-1 leading-none">{step.desc}</p>
                  </div>
                </div>
              ))}

              <div className="pt-2 px-1">
                <button
                  onClick={onTestOverlay}
                  className="w-full py-2.5 rounded-xl border border-dashed border-white/10 hover:border-purple-500/40 hover:bg-purple-500/5 text-[10px] font-black text-gray-500 hover:text-purple-400 uppercase tracking-widest transition-all"
                >
                  Send Test Signal
                </button>
              </div>
            </div>

            {/* Live Preview - Streamlined */}
            <div className="lg:col-span-8">
              <div className="h-full bg-black/40 border border-white/5 rounded-3xl overflow-hidden flex flex-col shadow-inner">
                <div className="px-4 py-2 bg-white/[0.02] border-b border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-purple-500 transition-shadow animate-pulse shadow-[0_0_8px_rgba(168,85,247,0.5)]" />
                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Live Overlay Preview</span>
                  </div>
                  <div className="flex items-center gap-3 opacity-60 hover:opacity-100 transition-opacity">
                    <span className="text-[9px] text-purple-400 font-black uppercase tracking-widest bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">Hidden until stats recorded</span>
                    <span className="text-[9px] text-gray-500 font-medium">Don't want visuals? Click <strong className="text-gray-400 font-bold">👁️</strong> in OBS</span>
                  </div>
                </div>

                <div className="flex-1 p-4 flex items-center justify-center min-h-[160px] bg-grid-white/[0.02]">
                  <div className="relative w-full h-full bg-[#050505] rounded-2xl border border-white/5 overflow-hidden flex items-center justify-center shadow-2xl">
                    <GameStatsOverlay
                      containerClassName="relative z-10 flex items-center justify-center w-full h-full"
                      scale={0.55 * (overlayScale / 2.0)}
                      stats={Object.keys(gameStats).length > 0 ? gameStats : {
                        victories: { value: 3, updatedAt: Date.now(), label: 'VICTORIES' },
                        deaths: { value: 12, updatedAt: Date.now() - 1000, label: 'DEATHS', lastUpdateMetadata: 'Killed by Lava' },
                        loot: { value: 5, updatedAt: Date.now() - 2000, label: 'LEGENDARY LOOT', lastUpdateMetadata: 'Excalibur' },
                        headshots: { value: 42, updatedAt: Date.now() - 3000, label: 'HEADSHOTS' }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Upgrade Modal Component
function UpgradeModal({ serverUrl, onClose, openBillingPortal }: { serverUrl: string; onClose: () => void; openBillingPortal: (e: React.MouseEvent) => void }) {
  const features = [
    { icon: '🎙️', title: 'AI Voice Responses', desc: 'Natural, engaging voice that interacts with your chat in real-time' },
    { icon: '🎭', title: 'Custom AI Personality', desc: 'Create a unique co-host that matches your stream vibe' },
    { icon: '🎮', title: 'Screen Vision AI', desc: 'AI can see your gameplay and comment on epic moments' },
    { icon: '💰', title: 'Monetization Alerts', desc: 'Thank donors, celebrate subs, and hype up bits automatically' },
    { icon: '🔊', title: 'Push-to-Talk', desc: 'Control when AI speaks with global hotkeys' },
    { icon: '⚡', title: 'Sub-100ms Latency', desc: 'Lightning-fast responses that feel natural' },
  ];

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="relative bg-gradient-to-b from-[#0f0f1a] to-[#0a0a0f] border border-purple-500/30 rounded-2xl p-5 md:p-6 max-w-xl mx-4 shadow-2xl shadow-purple-500/10 my-auto">
        {/* Animated background effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600/5 via-transparent to-pink-600/5 rounded-3xl pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-purple-600/20 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-pink-600/10 rounded-full blur-[80px] pointer-events-none" />

        <div className="relative">
          {/* Header */}
          <div className="text-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-600/30 to-pink-600/30 rounded-xl flex items-center justify-center mx-auto mb-4 border border-purple-500/30 shadow-lg shadow-purple-500/20">
              <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>

            <h3 className="text-2xl md:text-3xl font-black text-white mb-2 tracking-tight">
              Ready to Go Live?
            </h3>
            <p className="text-gray-400 text-base max-w-md mx-auto leading-relaxed">
              Your AI co-host is waiting. Subscribe to start streaming.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            {features.map((feature, i) => (
              <div key={i} className="flex items-start gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/30 rounded-lg p-2.5 transition-all duration-300 group">
                <div className="text-lg group-hover:scale-110 transition-transform shrink-0">{feature.icon}</div>
                <div className="min-w-0">
                  <div className="text-white font-bold text-xs mb-0.5">{feature.title}</div>
                  <div className="text-gray-500 text-[10px] leading-tight">{feature.desc}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Pricing & CTA */}
          <div className="bg-gradient-to-r from-purple-600/20 via-pink-600/20 to-purple-600/20 border border-purple-500/30 rounded-xl p-4 mb-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-4xl font-black text-white">$25</span>
                  <span className="text-gray-400">/month</span>
                </div>
                <div className="text-gray-500 text-sm flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Cancel anytime — no commitment
                </div>
              </div>
              <a
                href={`${serverUrl}/api/billing/portal`}
                onClick={openBillingPortal}
                className="w-full md:w-auto bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-black py-4 px-8 rounded-xl transition-all duration-300 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 hover:scale-105 text-center whitespace-nowrap"
              >
                Start Streaming Now
              </a>
            </div>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-3 text-[10px] text-gray-500 mb-3">
            <span className="flex items-center gap-1.5">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
              Secure Stripe checkout
            </span>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="w-full text-gray-500 hover:text-gray-300 text-sm font-medium py-3 transition-colors"
          >
            Maybe later — I'll keep the dashboard in preview mode
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Quick Start Guide ─────────────────────────────────────────────────────
